import { useEffect, useState, useMemo } from "react";
// (Admin.jsx — sub-panels appended at end of file)
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CATEGORIES, catLabel } from "@/lib/constants";
import { toast } from "sonner";
import { Users, Video, Flag, Megaphone, ShieldCheck, Check, X, MessageSquare, Mail, Phone, TrendingUp, IndianRupee, Ban, UserCheck, KeyRound, Eye, EyeOff, Trash2, RotateCcw, AlertTriangle, Sparkles, Search, Archive, CalendarClock, Send, FileClock, Settings2, Wallet, Loader2, Bell, Image as ImageIcon, LayoutGrid, Bot, Clock, Plus, Edit2, Save, Copy, Play, GraduationCap, ExternalLink, Folder, MapPin, HelpCircle } from "lucide-react";

const STATUS_META = {
  active:    { label: "Active",    cls: "bg-emerald-500/20 text-emerald-400" },
  suspended: { label: "Suspended", cls: "bg-amber-500/20 text-amber-400" },
  banned:    { label: "Banned",    cls: "bg-red-500/20 text-red-400" },
};
const VIDEO_STATUS = {
  live:           { label: "Live",           cls: "bg-emerald-500/20 text-emerald-400" },
  pending_review: { label: "Pending",        cls: "bg-amber-500/20 text-amber-400" },
  reported:       { label: "Reported",       cls: "bg-red-500/20 text-red-400" },
  hidden:         { label: "Hidden",         cls: "bg-zinc-700/40 text-zinc-400" },
};

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "dashboard");
  const [dash, setDash] = useState(null);
  const [users, setUsers] = useState([]);
  const [uQ, setUQ] = useState("");
  const [uCity, setUCity] = useState("");
  const [uStatus, setUStatus] = useState("");
  const [videos, setVideos] = useState([]);
  const [vStatus, setVStatus] = useState("");
  const [vQ, setVQ] = useState("");
  const [reports, setReports] = useState([]);
  const [castingForm, setCastingForm] = useState({ title: "", description: "", category: "actor", city: "", budget: "", deadline: "" });
  const [verifications, setVerifications] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [inqStatus, setInqStatus] = useState("all");
  const [inqCity, setInqCity] = useState("all");
  const [inqSearch, setInqSearch] = useState("");

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/dashboard").then(r => setDash(r.data));
    loadUsers();
    loadVideos();
    api.get("/admin/reports").then(r => setReports(r.data.reports || []));
    api.get("/admin/verifications").then(r => setVerifications(r.data.applications || []));
    api.get("/admin/casting-inquiries").then(r => setInquiries(r.data.inquiries || [])).catch(() => {});
    // eslint-disable-next-line
  }, [user]);

  const loadUsers = async () => {
    const params = new URLSearchParams();
    if (uQ) params.set("q", uQ);
    if (uCity) params.set("city", uCity);
    if (uStatus) params.set("status", uStatus);
    const { data } = await api.get(`/admin/users?${params.toString()}`);
    setUsers(data.users || []);
  };
  const loadVideos = async () => {
    const params = new URLSearchParams();
    if (vStatus) params.set("status", vStatus);
    if (vQ) params.set("q", vQ);
    const { data } = await api.get(`/admin/videos?${params.toString()}`);
    setVideos(data.videos || []);
  };

  // User actions
  const userAction = async (uid, action, reason) => {
    if (["suspend", "ban", "reset"].includes(action) && !confirm(`${action.toUpperCase()} this user?`)) return;
    try {
      const body = ["suspend", "ban"].includes(action) ? { reason: reason || "policy violation" } : {};
      await api.post(`/admin/users/${uid}/${action}`, body);
      toast.success(`User ${action}ed`);
      loadUsers();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const videoAction = async (vid, action) => {
    if (action === "delete" && !confirm("Delete (soft) this performance?")) return;
    try {
      if (action === "delete") await api.delete(`/admin/videos/${vid}`);
      else await api.post(`/admin/videos/${vid}/${action}`);
      toast.success(`Video ${action}d`);
      loadVideos();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const approveVerification = async (id) => {
    await api.post(`/admin/verifications/${id}/approve`);
    toast.success("Approved & verified");
    setVerifications(v => v.map(x => x.id === id ? { ...x, status: "approved" } : x));
  };
  const rejectVerification = async (id) => {
    const reason = prompt("Reason (optional)") || "";
    await api.post(`/admin/verifications/${id}/reject`, { reason });
    toast.success("Rejected");
    setVerifications(v => v.map(x => x.id === id ? { ...x, status: "rejected", rejection_reason: reason } : x));
  };
  const updateInquiryStatus = async (id, status) => {
    await api.post(`/admin/casting-inquiries/${id}/status?status=${status}`);
    toast.success(`Marked ${status}`);
    setInquiries(list => list.map(x => x.id === id ? { ...x, status } : x));
  };

  const postCasting = async () => {
    try {
      await api.post("/admin/casting", castingForm);
      toast.success("Casting role posted");
      setCastingForm({ title: "", description: "", category: "actor", city: "", budget: "", deadline: "" });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const hpReports = useMemo(() => reports.filter(r => r.high_priority), [reports]);
  const normalReports = useMemo(() => reports.filter(r => !r.high_priority), [reports]);

  if (!user) return null;
  if (!user.is_admin) {
    return <div className="min-h-screen"><TopBar/><p className="p-6 text-zinc-400 text-sm">Admin access only.</p></div>;
  }

  return (
    <div data-testid="admin-page" className="min-h-screen pb-24">
      <TopBar subtitle="Admin"/>
      <div className="max-w-6xl mx-auto px-4 pt-4">
        <BackButton fallback="/app" label="Back" className="mb-4"/>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); navigate(`/app/admin?tab=${v}`, { replace: true }); }}>
          <TabsList className="bg-[#180D0D] border border-zinc-800 flex-wrap h-auto overflow-x-auto">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="performances" data-testid="tab-performances">Performances</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">
              Reports {hpReports.length > 0 && <span className="ml-1.5 text-[10px] rounded-full bg-red-500 text-white px-1.5 animate-pulse">{hpReports.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="opportunities" data-testid="tab-opportunities">Opportunities</TabsTrigger>
            <TabsTrigger value="creator-pass" data-testid="tab-creator-pass">Creator Pass</TabsTrigger>
            <TabsTrigger value="revenue" data-testid="tab-revenue">Revenue</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">Audit</TabsTrigger>
            <TabsTrigger value="casting" data-testid="tab-casting">Casting</TabsTrigger>
            <TabsTrigger value="verifications" data-testid="tab-verifications">Verify {verifications.filter(v => v.status === "pending").length > 0 && <span className="ml-1.5 text-[10px] rounded-full bg-[#DC2626] text-white px-1.5">{verifications.filter(v => v.status === "pending").length}</span>}</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries {inquiries.filter(i => i.status === "new").length > 0 && <span className="ml-1.5 text-[10px] rounded-full bg-[#DC2626] text-white px-1.5">{inquiries.filter(i => i.status === "new").length}</span>}</TabsTrigger>
            <TabsTrigger value="notify" data-testid="tab-notify">Notify</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="banners" data-testid="tab-banners">Banners</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai">AI Helper</TabsTrigger>
            <TabsTrigger value="training-ads" data-testid="tab-training-ads">Training Ads</TabsTrigger>
            <TabsTrigger value="sponsored-ads" data-testid="tab-sponsored-ads">Sponsored</TabsTrigger>
            <TabsTrigger value="featured" data-testid="tab-featured">Featured</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="ad-dashboard" data-testid="tab-ad-dashboard">Ad Dashboard</TabsTrigger>
            <TabsTrigger value="opp-analytics" data-testid="tab-opp-analytics">Opp Analytics</TabsTrigger>
            <TabsTrigger value="advertise" data-testid="tab-advertise">Advertise Leads</TabsTrigger>
            <TabsTrigger value="support" data-testid="tab-support">Support</TabsTrigger>
            <TabsTrigger value="appeals" data-testid="tab-appeals">Appeals</TabsTrigger>
            <TabsTrigger value="search-analytics" data-testid="tab-search-analytics">Search</TabsTrigger>
            <TabsTrigger value="notif-log" data-testid="tab-notif-log">Notif Log</TabsTrigger>
          </TabsList>

          {/* ==================== DASHBOARD ==================== */}
          <TabsContent value="dashboard" className="mt-4">
            {!dash ? <p className="text-sm text-zinc-400">Loading dashboard…</p> : (
              <div data-testid="admin-dashboard" className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Metric testid="m-new-users"      label="New users today"       value={dash.new_users_today}         icon={Users}      accent="emerald"  to="/app/admin?tab=users"/>
                  <Metric testid="m-active-users"   label="Active creators today" value={dash.active_users_today}      icon={TrendingUp} accent="emerald"  to="/app/admin?tab=users"/>
                  <Metric testid="m-new-perfs"      label="New performances"      value={dash.new_performances_today}  icon={Video}      accent="red"      to="/app/admin?tab=performances"/>
                  <Metric testid="m-creator-pass"   label="Creator Pass sales"    value={dash.creator_pass_today}      icon={ShieldCheck} accent="gold"    to="/app/admin?tab=creator-pass"/>
                  <Metric testid="m-revenue"        label="Revenue today"         value={`₹${dash.revenue_today}`}     icon={IndianRupee} accent="gold"    to="/app/admin?tab=revenue"/>
                  <Metric testid="m-pending-reps"   label="Pending reports"       value={dash.pending_reports}         icon={Flag}       accent="red"      to="/app/admin?tab=reports"/>
                  <Metric testid="m-hp-reports"     label="High-priority reports" value={dash.high_priority_reports}   icon={AlertTriangle} accent="red"   to="/app/admin?tab=reports"/>
                  <Metric testid="m-campaigns"      label="Active campaigns"      value={dash.active_campaigns}        icon={Megaphone}  accent="red"      to="/app/admin?tab=casting"/>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Metric testid="m-total-users"    label="Total users"           value={dash.total_users}             icon={Users}      accent="zinc"     to="/app/admin?tab=users"/>
                  <Metric testid="m-total-videos"   label="Total performances"    value={dash.total_videos}            icon={Video}      accent="zinc"     to="/app/admin?tab=performances"/>
                  <Metric testid="m-private-mode"   label="Private mode"          value={dash.private_mode ? "ON" : "OFF"} icon={Sparkles} accent={dash.private_mode ? "amber" : "emerald"} to="/app/admin?tab=settings"/>
                </div>
                <p className="text-[11px] text-zinc-500">Generated at {new Date(dash.generated_at).toLocaleString()}</p>
              </div>
            )}
          </TabsContent>

          {/* ==================== PERFORMANCES ==================== */}
          <TabsContent value="performances" className="mt-4">
            <div data-testid="perf-filters" className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 space-y-3 mb-4">
              <div className="flex flex-wrap gap-2">
                {[{k: "", label: "All"}, {k: "live", label: "Live"}, {k: "pending_review", label: "Pending"}, {k: "reported", label: "Reported"}, {k: "hidden", label: "Hidden"}].map(s => (
                  <button key={s.k || "all"} data-testid={`vfilter-${s.k || "all"}`} onClick={() => { setVStatus(s.k); setTimeout(loadVideos, 0); }}
                    className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${vStatus === s.k ? "border-[#DC2626] bg-[#DC2626]/20 text-[#DC2626]" : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-[#DC2626]/40"}`}>
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"/>
                <Input data-testid="perf-search" value={vQ} onChange={(e) => setVQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadVideos()} placeholder="Search title, username, category…" className="pl-9 bg-zinc-900 border-zinc-800 h-9 text-sm"/>
              </div>
            </div>
            {videos.length === 0 ? <p className="text-sm text-zinc-400">No performances match.</p> : (
              <div className="space-y-2">
                {videos.map(v => {
                  const s = VIDEO_STATUS[v.status] || VIDEO_STATUS.live;
                  return (
                    <div key={v.id} data-testid={`perf-${v.id}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex items-center gap-3 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{v.title || "(Untitled)"}</p>
                        <p className="text-[11px] text-zinc-400 truncate">@{v.username} · {catLabel(v.category)} · {v.duration}s · {v.reports_count || 0} reports</p>
                      </div>
                      <span className={`text-[10px] rounded-full px-2 py-0.5 uppercase tracking-wide font-semibold ${s.cls}`}>{s.label}</span>
                      <div className="flex flex-wrap gap-1">
                        <Link to={`/v/${v.id}`} data-testid={`perf-view-${v.id}`} className="h-8 px-3 rounded-full bg-zinc-900 border border-zinc-700 text-xs text-white inline-flex items-center gap-1 hover:border-[#DC2626]/40"><Eye size={12}/> View</Link>
                        {v.status !== "hidden" ? (
                          <Button size="sm" data-testid={`perf-hide-${v.id}`} onClick={() => videoAction(v.id, "hide")} className="h-8 rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs"><EyeOff size={12} className="mr-1"/> Hide</Button>
                        ) : (
                          <Button size="sm" data-testid={`perf-restore-${v.id}`} onClick={() => videoAction(v.id, "restore")} className="h-8 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-xs"><RotateCcw size={12} className="mr-1"/> Restore</Button>
                        )}
                        <Button size="sm" data-testid={`perf-delete-${v.id}`} onClick={() => videoAction(v.id, "delete")} className="h-8 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs"><Trash2 size={12} className="mr-1"/> Delete</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ==================== USERS ==================== */}
          <TabsContent value="users" className="mt-4">
            <div data-testid="user-filters" className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 space-y-3 mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="relative sm:col-span-2">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"/>
                  <Input data-testid="user-search" value={uQ} onChange={(e) => setUQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadUsers()} placeholder="Name, @username, email, phone, city…" className="pl-9 bg-zinc-900 border-zinc-800 h-9 text-sm"/>
                </div>
                <Input data-testid="user-city" value={uCity} onChange={(e) => setUCity(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadUsers()} placeholder="City filter" className="bg-zinc-900 border-zinc-800 h-9 text-sm"/>
              </div>
              <div className="flex flex-wrap gap-2">
                {[{k: "", label: "All"}, {k: "active", label: "Active"}, {k: "suspended", label: "Suspended"}, {k: "banned", label: "Banned"}].map(s => (
                  <button key={s.k || "all"} data-testid={`ufilter-${s.k || "all"}`} onClick={() => { setUStatus(s.k); setTimeout(loadUsers, 0); }}
                    className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${uStatus === s.k ? "border-[#DC2626] bg-[#DC2626]/20 text-[#DC2626]" : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-[#DC2626]/40"}`}>
                    {s.label}
                  </button>
                ))}
                <Button size="sm" data-testid="user-apply" onClick={loadUsers} className="h-8 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white text-xs">Search</Button>
              </div>
            </div>
            {users.length === 0 ? <p className="text-sm text-zinc-400">No users match.</p> : (
              <div className="space-y-2">
                {users.map(u => {
                  const st = STATUS_META[u.account_status || "active"] || STATUS_META.active;
                  const plan = (u.plan || "free").toUpperCase();
                  return (
                    <div key={u.user_id} data-testid={`user-${u.user_id}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex items-center gap-3 flex-wrap">
                      <div className="w-9 h-9 rounded-full bg-[#DC2626]/15 border border-[#DC2626]/40 flex items-center justify-center text-[#DC2626] text-xs font-semibold shrink-0">{(u.name || u.username || "?").slice(0,1).toUpperCase()}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{u.name} {u.is_verified && <ShieldCheck size={12} className="inline text-[#F5C462]"/>}</p>
                        <p className="text-[11px] text-zinc-400 truncate">@{u.username} · {u.email || u.phone_number || "—"} · {u.profile?.city || "—"}</p>
                      </div>
                      <span className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${st.cls}`}>{st.label}</span>
                      <span className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${plan === "FREE" ? "bg-zinc-800 text-zinc-400" : plan === "PLUS" ? "bg-blue-500/20 text-blue-400" : "bg-[#F5C462]/20 text-[#F5C462]"}`}>Creator Pass: {plan}</span>
                      <div className="flex flex-wrap gap-1">
                        {u.account_status !== "active" ? (
                          <Button size="sm" data-testid={`user-activate-${u.user_id}`} onClick={() => userAction(u.user_id, "activate")} className="h-8 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-xs"><UserCheck size={12} className="mr-1"/> Activate</Button>
                        ) : (
                          <>
                            <Button size="sm" data-testid={`user-suspend-${u.user_id}`} onClick={() => userAction(u.user_id, "suspend")} className="h-8 rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs"><EyeOff size={12} className="mr-1"/> Suspend</Button>
                            <Button size="sm" data-testid={`user-ban-${u.user_id}`} onClick={() => userAction(u.user_id, "ban")} className="h-8 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs"><Ban size={12} className="mr-1"/> Ban</Button>
                          </>
                        )}
                        <Button size="sm" data-testid={`user-reset-${u.user_id}`} onClick={() => userAction(u.user_id, "reset")} className="h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs"><KeyRound size={12} className="mr-1"/> Reset</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ==================== REPORTS ==================== */}
          <TabsContent value="reports" className="mt-4">
            {reports.length === 0 ? <p className="text-sm text-zinc-400">No reports yet.</p> : (
              <div className="space-y-4">
                {hpReports.length > 0 && (
                  <div data-testid="hp-section">
                    <p className="text-xs uppercase tracking-wider text-red-400 flex items-center gap-1 mb-2"><AlertTriangle size={12}/> High priority — 10+ reports</p>
                    <div className="space-y-2">
                      {hpReports.map(r => <ReportRow key={r.target_id} r={r} onAction={videoAction}/>)}
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-xs uppercase tracking-wider text-zinc-400 mb-2">All reports</p>
                  <div className="space-y-2">
                    {normalReports.map(r => <ReportRow key={r.target_id} r={r} onAction={videoAction}/>)}
                    {normalReports.length === 0 && <p className="text-sm text-zinc-500">All caught up outside the high-priority queue.</p>}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ==================== OPPORTUNITIES ==================== */}
          <TabsContent value="opportunities" className="mt-4"><OpportunitiesPanel/></TabsContent>

          {/* ==================== CREATOR PASS ==================== */}
          <TabsContent value="creator-pass" className="mt-4"><CreatorPassPanel users={users} loadUsers={loadUsers}/></TabsContent>

          {/* ==================== REVENUE ==================== */}
          <TabsContent value="revenue" className="mt-4"><RevenuePanel/></TabsContent>

          {/* ==================== SETTINGS ==================== */}
          <TabsContent value="settings" className="mt-4"><SettingsPanel/></TabsContent>

          {/* ==================== AUDIT LOG ==================== */}
          <TabsContent value="audit" className="mt-4"><AuditPanel/></TabsContent>

          {/* ==================== CASTING ==================== */}
          <TabsContent value="casting" className="mt-4">
            <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4 space-y-3">
              <p className="font-display font-semibold text-white">Post a casting role</p>
              <Input data-testid="casting-title" placeholder="Title" value={castingForm.title} onChange={(e) => setCastingForm({ ...castingForm, title: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
              <Textarea data-testid="casting-desc" placeholder="Description / requirements" value={castingForm.description} onChange={(e) => setCastingForm({ ...castingForm, description: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
              <div className="grid grid-cols-2 gap-2">
                <Select value={castingForm.category} onValueChange={(v) => setCastingForm({ ...castingForm, category: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Category"/></SelectTrigger>
                  <SelectContent className="bg-[#180D0D] border-zinc-800 text-white max-h-64">
                    {CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input data-testid="casting-city" placeholder="City" value={castingForm.city} onChange={(e) => setCastingForm({ ...castingForm, city: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="casting-budget" placeholder="Budget" value={castingForm.budget} onChange={(e) => setCastingForm({ ...castingForm, budget: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
                <Input data-testid="casting-deadline" placeholder="Deadline (YYYY-MM-DD)" value={castingForm.deadline} onChange={(e) => setCastingForm({ ...castingForm, deadline: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
              </div>
              <Button data-testid="casting-post" onClick={postCasting} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold"><Megaphone size={14} className="mr-1"/> Post</Button>
            </div>
          </TabsContent>

          {/* ==================== VERIFICATIONS ==================== */}
          <TabsContent value="verifications" className="mt-4">
            {verifications.length === 0 ? <p className="text-sm text-zinc-400">No verification requests.</p> : (
              <div className="space-y-2">
                {verifications.map(v => (
                  <div key={v.id} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">@{v.username} · ₹{v.amount}</p>
                      <p className="text-[11px] text-zinc-400">{v.status} · {new Date(v.created_at).toLocaleString()}</p>
                    </div>
                    {v.status === "pending" && (
                      <>
                        <Button size="sm" onClick={() => approveVerification(v.id)} className="h-8 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-xs"><Check size={12} className="mr-1"/> Approve</Button>
                        <Button size="sm" onClick={() => rejectVerification(v.id)} className="h-8 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs"><X size={12} className="mr-1"/> Reject</Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ==================== INQUIRIES ==================== */}
          <TabsContent value="inquiries" className="mt-4">
            {inquiries.length === 0 ? <p className="text-sm text-zinc-400">No casting inquiries yet.</p> : (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {["all", "new", "contacted", "closed"].map(s => (
                    <button key={s} data-testid={`inq-filter-status-${s}`} onClick={() => setInqStatus(s)} className={`h-8 px-3 rounded-full text-xs font-medium border ${inqStatus === s ? "border-[#DC2626] bg-[#DC2626]/20 text-[#DC2626]" : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-[#DC2626]/40"}`}>
                      {s[0].toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>
                <Input data-testid="inq-search" value={inqSearch} onChange={(e) => setInqSearch(e.target.value)} placeholder="Search…" className="bg-zinc-900 border-zinc-800 h-9 text-sm"/>
                {inquiries
                  .filter(i => inqStatus === "all" || i.status === inqStatus)
                  .filter(i => !inqSearch || (i.role + i.target_username + (i.contact_name || "") + (i.company || "")).toLowerCase().includes(inqSearch.toLowerCase()))
                  .map(i => (
                    <div key={i.id} data-testid={`inquiry-${i.id}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs uppercase tracking-wider text-[#DC2626] flex items-center gap-1"><MessageSquare size={12}/> {i.role}</p>
                          <p className="font-semibold mt-1">For @{i.target_username}</p>
                          <p className="text-sm text-zinc-200 mt-2 leading-relaxed">{i.requirements}</p>
                          <div className="mt-3 flex flex-wrap gap-3 text-xs text-zinc-300 border-t border-zinc-800 pt-2">
                            <span>{i.contact_name}</span>
                            <a href={`mailto:${i.contact_email}`} className="text-[#DC2626]"><Mail size={11} className="inline mr-0.5"/>{i.contact_email}</a>
                            {i.contact_phone && <a href={`tel:${i.contact_phone}`} className="text-emerald-400"><Phone size={11} className="inline mr-0.5"/>{i.contact_phone}</a>}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <span className={`text-[10px] rounded-full px-2 py-0.5 uppercase tracking-wide ${i.status === "new" ? "bg-[#DC2626]/20 text-[#DC2626]" : i.status === "contacted" ? "bg-amber-500/20 text-amber-400" : "bg-zinc-700/40 text-zinc-400"}`}>{i.status}</span>
                          {i.status !== "closed" && (
                            <div className="flex gap-1">
                              {i.status === "new" && <Button size="sm" onClick={() => updateInquiryStatus(i.id, "contacted")} className="h-7 rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs">Contacted</Button>}
                              <Button size="sm" onClick={() => updateInquiryStatus(i.id, "closed")} className="h-7 rounded-full bg-zinc-700/40 hover:bg-zinc-700 text-zinc-300 text-xs">Close</Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </TabsContent>

          {/* ==================== NOTIFICATIONS CENTRE ==================== */}
          <TabsContent value="notify" className="mt-4"><NotifyPanel/></TabsContent>

          {/* ==================== ANALYTICS ==================== */}
          <TabsContent value="analytics" className="mt-4"><AnalyticsPanel/></TabsContent>

          {/* ==================== BANNER MANAGER ==================== */}
          <TabsContent value="banners" className="mt-4"><BannersPanel/></TabsContent>

          {/* ==================== CATEGORIES ==================== */}
          <TabsContent value="categories" className="mt-4"><CategoriesPanel/></TabsContent>

          {/* ==================== AI CONTENT HELPER ==================== */}
          <TabsContent value="ai" className="mt-4"><AiHelperPanel/></TabsContent>

          {/* ==================== TRAINING ADVERTISEMENTS ==================== */}
          <TabsContent value="training-ads" className="mt-4"><TrainingAdsPanel/></TabsContent>

          {/* ==================== SPONSORED ADS ==================== */}
          <TabsContent value="sponsored-ads" className="mt-4"><SponsoredAdsPanel/></TabsContent>

          {/* ==================== FEATURED LISTINGS ==================== */}
          <TabsContent value="featured" className="mt-4"><FeaturedListingsPanel/></TabsContent>

          {/* ==================== TRAINING AD ENQUIRIES ==================== */}
          <TabsContent value="enquiries" className="mt-4"><EnquiriesPanel/></TabsContent>

          {/* ==================== AD DASHBOARD ==================== */}
          <TabsContent value="ad-dashboard" className="mt-4"><AdDashboardPanel/></TabsContent>

          {/* ==================== OPPORTUNITY ANALYTICS ==================== */}
          <TabsContent value="opp-analytics" className="mt-4"><OppAnalyticsPanel/></TabsContent>

          {/* ==================== ADVERTISE LEADS ==================== */}
          <TabsContent value="advertise" className="mt-4"><AdvertiseLeadsPanel/></TabsContent>

          {/* ==================== SUPPORT TICKETS ==================== */}
          <TabsContent value="support" className="mt-4"><SupportTicketsPanel/></TabsContent>

          {/* ==================== APPEALS ==================== */}
          <TabsContent value="appeals" className="mt-4"><AppealsPanel/></TabsContent>

          {/* ==================== SEARCH ANALYTICS ==================== */}
          <TabsContent value="search-analytics" className="mt-4"><SearchAnalyticsPanel/></TabsContent>

          {/* ==================== NOTIFICATIONS LOG ==================== */}
          <TabsContent value="notif-log" className="mt-4"><NotificationsLogPanel/></TabsContent>

        </Tabs>
      </div>
    </div>
  );
}

function Metric({ testid, label, value, icon: Icon, accent = "red", to }) {
  const accents = {
    red:     "border-[#DC2626]/40 bg-[#DC2626]/8 text-[#DC2626]",
    gold:    "border-[#F5C462]/40 bg-[#F5C462]/8 text-[#F5C462]",
    emerald: "border-emerald-500/40 bg-emerald-500/8 text-emerald-400",
    amber:   "border-amber-500/40 bg-amber-500/8 text-amber-400",
    zinc:    "border-zinc-700 bg-zinc-900/60 text-zinc-400",
  };
  const inner = (
    <>
      <div className={`w-9 h-9 rounded-xl inline-flex items-center justify-center border ${accents[accent]} mb-2`}>
        <Icon size={16}/>
      </div>
      <p className="text-xs text-zinc-400 uppercase tracking-wider leading-tight">{label}</p>
      <p className="font-display text-2xl font-bold text-white mt-0.5">{value}</p>
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testid} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4 hover:border-[#DC2626]/40 transition-colors block">
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={testid} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4">
      {inner}
    </div>
  );
}

function ReportRow({ r, onAction }) {
  const v = r.video;
  return (
    <div data-testid={`report-${r.target_id}`} className={`rounded-2xl border p-3 ${r.high_priority ? "border-red-500/50 bg-red-500/5" : "border-zinc-800 bg-[#180D0D]"} flex items-center gap-3 flex-wrap`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate">
          {r.high_priority && <span className="text-red-400 mr-1">🚨</span>}
          {v?.title || "(Deleted video)"} <span className="text-xs text-zinc-400 font-normal">· @{v?.username || "—"}</span>
        </p>
        <p className="text-[11px] text-zinc-400 mt-0.5">
          <span className="text-red-400 font-semibold">{r.count} reports</span> from {r.reporter_count} users
          {r.reasons.length > 0 && <> · Reasons: {r.reasons.slice(0, 3).join(", ")}</>}
        </p>
      </div>
      {v && (
        <div className="flex flex-wrap gap-1">
          <Link to={`/v/${v.id}`} data-testid={`report-view-${v.id}`} className="h-8 px-3 rounded-full bg-zinc-900 border border-zinc-700 text-xs text-white inline-flex items-center gap-1"><Eye size={12}/> View</Link>
          {r.status !== "hidden" ? (
            <Button size="sm" data-testid={`report-hide-${v.id}`} onClick={() => onAction(v.id, "hide")} className="h-8 rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs"><EyeOff size={12} className="mr-1"/> Hide</Button>
          ) : (
            <Button size="sm" data-testid={`report-restore-${v.id}`} onClick={() => onAction(v.id, "restore")} className="h-8 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-xs"><RotateCcw size={12} className="mr-1"/> Restore</Button>
          )}
          <Button size="sm" data-testid={`report-delete-${v.id}`} onClick={() => onAction(v.id, "delete")} className="h-8 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs"><Trash2 size={12} className="mr-1"/> Delete</Button>
        </div>
      )}
    </div>
  );
}

// ==================== Sub-panels ====================
function OpportunitiesPanel() {
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [limit, setLimit] = useState(500);
  const [form, setForm] = useState({ title: "", category: "actor", sub_category: "", description: "", difficulty: "medium", duration: "", expiry: "", creator_pass_required: false, status: "draft", scheduled_at: "" });
  // Duration composite: number + unit
  const [durNum, setDurNum] = useState("");
  const [durUnit, setDurUnit] = useState("days");
  // Datetime-local values (browser gives YYYY-MM-DDTHH:mm — convert to ISO before POST)
  const [expiryLocal, setExpiryLocal] = useState("");
  const [scheduleLocal, setScheduleLocal] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async () => { const { data } = await api.get("/admin/opportunities"); setRows(data.opportunities || []); setTotal(data.total || 0); setLimit(data.limit || 500); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.title || !form.description) return toast.error("Title + description required");
    const durationStr = durNum ? `${durNum} ${durUnit}` : "";
    const toIso = (dtLocal) => dtLocal ? new Date(dtLocal).toISOString() : "";
    if (form.status === "scheduled" && !scheduleLocal) return toast.error("Please pick a schedule date/time");
    const payload = { ...form, duration: durationStr, expiry: toIso(expiryLocal), scheduled_at: toIso(scheduleLocal) };
    setBusy(true);
    try {
      await api.post("/admin/opportunities", payload);
      toast.success("Opportunity created");
      setForm({ ...form, title: "", description: "", sub_category: "" });
      setDurNum(""); setExpiryLocal(""); setScheduleLocal("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const setStatus = async (id, s) => { await api.post(`/admin/opportunities/${id}/status?status=${s}`); toast.success(`Marked ${s}`); load(); };
  return (
    <div data-testid="opps-panel" className="space-y-4">
      <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4 space-y-2">
        <p className="text-xs uppercase tracking-wider text-[#DC2626]">Create opportunity <span className="ml-2 text-zinc-500 font-normal">{total}/{limit} used</span></p>
        <Input data-testid="opp-title" placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        <div className="grid grid-cols-2 gap-2">
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue/></SelectTrigger><SelectContent className="bg-[#180D0D] border-zinc-800 text-white max-h-64">{CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent></Select>
          <Input data-testid="opp-subcat" placeholder="Sub category" value={form.sub_category} onChange={(e) => setForm({ ...form, sub_category: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        </div>
        <Textarea data-testid="opp-desc" placeholder="Description *" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        <div className="grid grid-cols-2 gap-2">
          <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}><SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue/></SelectTrigger><SelectContent className="bg-[#180D0D] border-zinc-800 text-white"><SelectItem value="easy">Easy</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="hard">Hard</SelectItem></SelectContent></Select>
          <div className="flex gap-2" data-testid="opp-duration-composite">
            <Input data-testid="opp-duration-num" type="number" min="1" placeholder="Duration" value={durNum} onChange={(e) => setDurNum(e.target.value)} className="bg-zinc-900 border-zinc-800 flex-1"/>
            <Select value={durUnit} onValueChange={setDurUnit}>
              <SelectTrigger data-testid="opp-duration-unit" className="bg-zinc-900 border-zinc-800 w-28"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-[#180D0D] border-zinc-800 text-white">
                <SelectItem value="hours">Hours</SelectItem>
                <SelectItem value="days">Days</SelectItem>
                <SelectItem value="months">Months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 block">Expiry</Label>
            <Input data-testid="opp-expiry" type="datetime-local" value={expiryLocal} onChange={(e) => setExpiryLocal(e.target.value)} className="bg-zinc-900 border-zinc-800 text-white"/>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1 block">Schedule (for status=scheduled)</Label>
            <Input data-testid="opp-schedule" type="datetime-local" value={scheduleLocal} onChange={(e) => setScheduleLocal(e.target.value)} className="bg-zinc-900 border-zinc-800 text-white"/>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs text-zinc-300 flex items-center gap-2"><input type="checkbox" data-testid="opp-cp" checked={form.creator_pass_required} onChange={(e) => setForm({ ...form, creator_pass_required: e.target.checked })}/> Creator Pass required</label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger className="bg-zinc-900 border-zinc-800 w-40"><SelectValue/></SelectTrigger><SelectContent className="bg-[#180D0D] border-zinc-800 text-white"><SelectItem value="draft">Draft</SelectItem><SelectItem value="scheduled">Scheduled</SelectItem><SelectItem value="published">Publish now</SelectItem></SelectContent></Select>
          <Button data-testid="opp-create" onClick={create} disabled={busy} className="ml-auto h-9 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">Save</Button>
        </div>
      </div>
      {rows.map(o => (
        <div key={o.id} data-testid={`opp-${o.id}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{o.title} {o.creator_pass_required && <ShieldCheck size={11} className="inline text-[#F5C462]"/>}</p><p className="text-[11px] text-zinc-400 truncate">{catLabel(o.category)} · {o.difficulty} · {o.duration || "—"}</p></div>
          <span className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${o.status === "published" ? "bg-emerald-500/20 text-emerald-400" : o.status === "scheduled" ? "bg-amber-500/20 text-amber-400" : o.status === "archived" ? "bg-zinc-700/40 text-zinc-400" : "bg-zinc-800 text-zinc-300"}`}>{o.status}</span>
          <div className="flex gap-1">
            {o.status !== "published" && <Button size="sm" onClick={() => setStatus(o.id, "published")} className="h-8 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-xs"><Send size={12} className="mr-1"/>Publish</Button>}
            {o.status !== "archived" && <Button size="sm" onClick={() => setStatus(o.id, "archived")} className="h-8 rounded-full bg-zinc-700/40 hover:bg-zinc-700 text-zinc-300 text-xs"><Archive size={12} className="mr-1"/>Archive</Button>}
          </div>
        </div>
      ))}
    </div>
  );
}

function CreatorPassPanel({ users, loadUsers }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/creator-pass/summary").then(r => setS(r.data)); }, []);
  const activate = async (uid) => { await api.post(`/admin/users/${uid}/creator-pass/activate`, {}); toast.success("Creator Pass activated"); loadUsers?.(); };
  const deactivate = async (uid) => { await api.post(`/admin/users/${uid}/creator-pass/deactivate`, {}); toast.success("Creator Pass deactivated"); loadUsers?.(); };
  return (
    <div data-testid="cp-panel" className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Metric testid="cp-active" label="Active passes" value={s?.active_passes ?? "—"} icon={ShieldCheck} accent="gold"/>
        <Metric testid="cp-expired" label="Expired today" value={s?.expired_today ?? "—"} icon={CalendarClock} accent="amber"/>
        <Metric testid="cp-renewals" label="Renewals today" value={s?.renewals_today ?? "—"} icon={TrendingUp} accent="emerald"/>
        <Metric testid="cp-price" label="Current price" value={s ? `₹${s.settings_price}` : "—"} icon={IndianRupee} accent="red"/>
      </div>
      <p className="text-xs text-zinc-400">Manually toggle Creator Pass for any user (emergencies / offline payments).</p>
      <div className="space-y-2">
        {(users || []).slice(0, 40).map(u => (
          <div key={u.user_id} data-testid={`cp-row-${u.user_id}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{u.name}</p><p className="text-[11px] text-zinc-400 truncate">@{u.username} · {u.plan?.toUpperCase() || "FREE"} {u.plan_expires_at && ` · until ${new Date(u.plan_expires_at).toLocaleDateString()}`}</p></div>
            {(u.plan === "free" || !u.plan) ? (
              <Button size="sm" data-testid={`cp-activate-${u.user_id}`} onClick={() => activate(u.user_id)} className="h-8 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-xs">Activate 30d</Button>
            ) : (
              <Button size="sm" data-testid={`cp-deactivate-${u.user_id}`} onClick={() => deactivate(u.user_id)} className="h-8 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs">Deactivate</Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RevenuePanel() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/admin/revenue").then(x => setR(x.data)); }, []);
  const Section = ({ title, d, testid }) => (
    <div data-testid={testid} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4">
      <p className="text-xs uppercase tracking-wider text-[#F5C462] flex items-center gap-1"><Wallet size={12}/> {title}</p>
      <p className="mt-2 font-display text-3xl font-bold text-white">₹{d?.total ?? 0}</p>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <div className="rounded-lg bg-zinc-900/70 border border-zinc-800 p-2"><p className="text-zinc-400">Creator Pass</p><p className="text-white font-semibold">₹{d?.creator_pass ?? 0}</p></div>
        <div className="rounded-lg bg-zinc-900/70 border border-zinc-800 p-2"><p className="text-zinc-400">Spotlight</p><p className="text-white font-semibold">₹{d?.spotlight ?? 0}</p></div>
        <div className="rounded-lg bg-zinc-900/70 border border-zinc-800 p-2"><p className="text-zinc-400">Other</p><p className="text-white font-semibold">₹{d?.other ?? 0}</p></div>
      </div>
      <p className="mt-2 text-[10px] text-zinc-500">{d?.count ?? 0} successful payments</p>
    </div>
  );
  return <div data-testid="revenue-panel" className="grid grid-cols-1 md:grid-cols-3 gap-3">
    <Section title="Today" d={r?.today} testid="rev-today"/>
    <Section title="This week" d={r?.week} testid="rev-week"/>
    <Section title="This month" d={r?.month} testid="rev-month"/>
  </div>;
}

function SettingsPanel() {
  const [s, setS] = useState(null); const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(r => setS(r.data)); }, []);
  const save = async () => { setBusy(true); try { const { data } = await api.post("/admin/settings", { creator_pass_price: Number(s.creator_pass_price), spotlight_price: Number(s.spotlight_price), monthly_opportunity_limit: Number(s.monthly_opportunity_limit), push_notification_limit: Number(s.push_notification_limit) }); setS(data.settings); toast.success("Settings updated"); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  if (!s) return <p className="text-sm text-zinc-400">Loading…</p>;
  const Row = ({ k, label, prefix }) => (
    <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4"><Label className="text-zinc-300 text-xs">{label}</Label><div className="mt-1 flex items-center gap-2">{prefix && <span className="text-zinc-500 text-sm">{prefix}</span>}<Input data-testid={`s-${k}`} type="number" min={0} value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="bg-zinc-900 border-zinc-800"/></div></div>
  );
  return <div data-testid="settings-panel" className="space-y-3">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Row k="creator_pass_price" label="Creator Pass price" prefix="₹"/>
      <Row k="spotlight_price" label="Featured / Spotlight price" prefix="₹"/>
      <Row k="monthly_opportunity_limit" label="Monthly opportunity limit"/>
      <Row k="push_notification_limit" label="Push notification limit / user / day"/>
    </div>
    <Button data-testid="settings-save" onClick={save} disabled={busy} className="h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold px-8">{busy ? <Loader2 className="animate-spin" size={14}/> : <><Settings2 size={14} className="mr-1"/> Save settings</>}</Button>
    <p className="text-[11px] text-zinc-500">Changes reflect immediately across the platform.</p>
  </div>;
}

function NotifyPanel() {
  const [groupType, setGroupType] = useState("all");
  const [category, setCategory] = useState("actor");
  const [plan, setPlan] = useState("free");
  const [userId, setUserId] = useState("");
  const [channel, setChannel] = useState("in_app");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [previewCount, setPreviewCount] = useState(null);

  const resolveGroup = () => {
    if (groupType === "all") return "all";
    if (groupType === "category") return `category:${category}`;
    if (groupType === "plan") return `plan:${plan}`;
    if (groupType === "single") return `single:${userId.trim()}`;
    return "all";
  };

  const send = async () => {
    if (!title.trim() || !message.trim()) { toast.error("Title aur message dono chahiye"); return; }
    if (groupType === "single" && !userId.trim()) { toast.error("User ID enter karein"); return; }
    if (!confirm(`Send "${title}" via ${channel.replace("_", "-")} to selected recipients?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/notify", { group: resolveGroup(), channel, title: title.trim(), message: message.trim() });
      setLastResult(data);
      toast.success(`Delivered — in-app: ${data.delivered_in_app}, WhatsApp: ${data.delivered_whatsapp}`);
      setTitle(""); setMessage("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    } finally { setBusy(false); }
  };

  // Rough preview via admin users endpoint (client-side approx; single/plan/category counts)
  useEffect(() => {
    let cancelled = false;
    const preview = async () => {
      try {
        const params = new URLSearchParams();
        if (groupType === "category") params.set("q", ""); // reuse users endpoint minimally
        const { data } = await api.get("/admin/users");
        if (cancelled) return;
        let count = 0;
        const arr = data.users || [];
        if (groupType === "all") count = arr.filter(u => u.account_status !== "banned").length;
        else if (groupType === "category") count = arr.filter(u => u.account_status !== "banned" && u.category === category).length;
        else if (groupType === "plan") count = arr.filter(u => u.account_status !== "banned" && (u.plan || "free") === plan).length;
        else if (groupType === "single") count = userId.trim() ? 1 : 0;
        setPreviewCount(count);
      } catch { if (!cancelled) setPreviewCount(null); }
    };
    preview();
    return () => { cancelled = true; };
  }, [groupType, category, plan, userId]);

  return (
    <div data-testid="notify-panel" className="space-y-4">
      <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Bell size={16} className="text-[#DC2626]"/>
          <p className="font-display font-semibold text-white">Broadcast notification</p>
        </div>

        {/* Group type */}
        <div>
          <Label className="text-zinc-300 text-xs">Audience</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {[
              { k: "all", label: "All users" },
              { k: "category", label: "By category" },
              { k: "plan", label: "By plan" },
              { k: "single", label: "Single user" },
            ].map(o => (
              <button key={o.k} data-testid={`notify-group-${o.k}`} onClick={() => setGroupType(o.k)}
                className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${groupType === o.k ? "border-[#DC2626] bg-[#DC2626]/20 text-[#DC2626]" : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-[#DC2626]/40"}`}>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {groupType === "category" && (
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="notify-category" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Category"/></SelectTrigger>
            <SelectContent className="bg-[#180D0D] border-zinc-800 text-white max-h-64">
              {CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {groupType === "plan" && (
          <Select value={plan} onValueChange={setPlan}>
            <SelectTrigger data-testid="notify-plan" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Plan"/></SelectTrigger>
            <SelectContent className="bg-[#180D0D] border-zinc-800 text-white">
              <SelectItem value="free">Free</SelectItem>
              <SelectItem value="plus">Plus</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
            </SelectContent>
          </Select>
        )}
        {groupType === "single" && (
          <Input data-testid="notify-user-id" value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="User ID (e.g. user_ab12cd34ef56)" className="bg-zinc-900 border-zinc-800"/>
        )}

        {previewCount !== null && (
          <div data-testid="notify-preview" className="text-[11px] text-zinc-400">
            ≈ <span className="text-white font-semibold">{previewCount}</span> recipient{previewCount === 1 ? "" : "s"} in current view
            <span className="text-zinc-600 ml-1">(final count is server-side)</span>
          </div>
        )}

        {/* Channel */}
        <div>
          <Label className="text-zinc-300 text-xs">Channel</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {[
              { k: "in_app", label: "In-app", icon: Bell },
              { k: "whatsapp", label: "WhatsApp (mocked)", icon: MessageSquare },
              { k: "both", label: "Both", icon: Send },
            ].map(o => {
              const Icon = o.icon;
              return (
                <button key={o.k} data-testid={`notify-channel-${o.k}`} onClick={() => setChannel(o.k)}
                  className={`h-8 px-3 rounded-full text-xs font-medium border inline-flex items-center gap-1 transition-colors ${channel === o.k ? "border-[#DC2626] bg-[#DC2626]/20 text-[#DC2626]" : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-[#DC2626]/40"}`}>
                  <Icon size={12}/> {o.label}
                </button>
              );
            })}
          </div>
        </div>

        <Input data-testid="notify-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. New Bollywood casting call!)" className="bg-zinc-900 border-zinc-800" maxLength={120}/>
        <Textarea data-testid="notify-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message body — Hinglish OK" className="bg-zinc-900 border-zinc-800 min-h-[100px]" maxLength={600}/>
        <div className="flex items-center justify-between text-[11px] text-zinc-500">
          <span>{title.length}/120 title · {message.length}/600 msg</span>
          <span>Only non-banned users receive</span>
        </div>

        <Button data-testid="notify-send" onClick={send} disabled={busy || !title.trim() || !message.trim()} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
          {busy ? <Loader2 className="animate-spin" size={14}/> : <><Send size={14} className="mr-1"/> Broadcast now</>}
        </Button>
      </div>

      {lastResult && (
        <div data-testid="notify-last-result" className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-4">
          <p className="text-xs uppercase tracking-wider text-emerald-400 mb-2">Last broadcast</p>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><p className="text-zinc-400 text-[11px]">Recipients</p><p className="text-white font-semibold text-lg">{lastResult.recipients}</p></div>
            <div><p className="text-zinc-400 text-[11px]">In-app sent</p><p className="text-emerald-400 font-semibold text-lg">{lastResult.delivered_in_app}</p></div>
            <div><p className="text-zinc-400 text-[11px]">WhatsApp sent</p><p className="text-[#25D366] font-semibold text-lg">{lastResult.delivered_whatsapp}</p></div>
          </div>
          <p className="text-[10px] text-zinc-500 mt-2">WhatsApp is mocked — messages logged to <code className="text-zinc-300">whatsapp_log</code>.</p>
        </div>
      )}
    </div>
  );
}

// ==================== ANALYTICS PANEL ====================
function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics").then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.detail || "Failed"));
  }, []);
  if (err) return <p data-testid="analytics-error" className="text-sm text-red-400">{err}</p>;
  if (!data) return <p className="text-sm text-zinc-400">Loading analytics…</p>;
  const Bar = ({ label, value, max, color = "#DC2626", testid }) => {
    const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
    return (
      <div data-testid={testid} className="space-y-1">
        <div className="flex items-baseline justify-between">
          <span className="text-xs text-zinc-400 uppercase tracking-wider">{label}</span>
          <span className="text-white font-semibold">{value.toLocaleString("en-IN")}</span>
        </div>
        <div className="h-2 rounded-full bg-zinc-900 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}, ${color}CC)` }}/>
        </div>
      </div>
    );
  };
  const maxDauMau = Math.max(data.dau, data.wau, data.mau, 1);
  return (
    <div data-testid="analytics-panel" className="space-y-4">
      {/* Headline KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric testid="a-dau" label="DAU" value={data.dau} icon={TrendingUp} accent="emerald"/>
        <Metric testid="a-wau" label="WAU" value={data.wau} icon={TrendingUp} accent="red"/>
        <Metric testid="a-mau" label="MAU" value={data.mau} icon={TrendingUp} accent="gold"/>
        <Metric testid="a-watch" label="Watch hours" value={data.total_watch_hours.toLocaleString("en-IN")} icon={Clock} accent="amber"/>
      </div>

      {/* Active-users chart */}
      <div data-testid="active-users-chart" className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4 space-y-3">
        <p className="text-xs uppercase tracking-wider text-[#DC2626]">Active users trend</p>
        <Bar label="Daily (24h)"   value={data.dau} max={maxDauMau} color="#10B981" testid="bar-dau"/>
        <Bar label="Weekly (7d)"   value={data.wau} max={maxDauMau} color="#DC2626" testid="bar-wau"/>
        <Bar label="Monthly (30d)" value={data.mau} max={maxDauMau} color="#F5C462" testid="bar-mau"/>
      </div>

      {/* Conversion & watch */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div data-testid="conversion-card" className="rounded-2xl border border-[#F5C462]/30 bg-gradient-to-br from-[#F5C462]/8 to-transparent p-4">
          <p className="text-xs uppercase tracking-wider text-[#F5C462] flex items-center gap-1"><Wallet size={12}/> Creator Pass conversion</p>
          <p className="mt-2 font-display text-4xl font-bold text-white">{data.creator_pass_conversion}%</p>
          <p className="text-[11px] text-zinc-500 mt-1">Paid users / total users</p>
        </div>
        <div data-testid="watch-card" className="rounded-2xl border border-[#DC2626]/30 bg-gradient-to-br from-[#DC2626]/8 to-transparent p-4">
          <p className="text-xs uppercase tracking-wider text-[#DC2626] flex items-center gap-1"><Clock size={12}/> Total watch time</p>
          <p className="mt-2 font-display text-4xl font-bold text-white">{data.total_watch_hours.toLocaleString("en-IN")}<span className="text-lg text-zinc-500 ml-1">hrs</span></p>
          <p className="text-[11px] text-zinc-500 mt-1">{(data.total_watch_seconds).toLocaleString("en-IN")} seconds cumulative</p>
        </div>
      </div>

      {/* Top 5 talents */}
      <div data-testid="top-viewed-panel" className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4">
        <p className="text-xs uppercase tracking-wider text-[#DC2626] mb-3 flex items-center gap-1"><Eye size={12}/> Top-5 most viewed talents</p>
        {data.top_viewed_talents.length === 0 ? <p className="text-sm text-zinc-500">No view data yet.</p> : (
          <ul className="space-y-2">
            {data.top_viewed_talents.map((t, i) => (
              <li key={t.user_id} data-testid={`top-viewed-${i}`} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-[#DC2626]/15 text-[#DC2626] font-bold text-xs flex items-center justify-center border border-[#DC2626]/30">{i+1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{t.name || `@${t.username}`}</p>
                  <p className="text-[11px] text-zinc-400">@{t.username || "—"}</p>
                </div>
                <span className="text-sm text-white font-semibold">{(t.views || 0).toLocaleString("en-IN")} <span className="text-[11px] text-zinc-500 font-normal">views</span></span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Top 5 creators */}
      <div data-testid="top-creators-panel" className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4">
        <p className="text-xs uppercase tracking-wider text-[#F5C462] mb-3 flex items-center gap-1"><Users size={12}/> Top-5 followed creators <span className="text-zinc-500 text-[10px] normal-case font-normal">(via qualified referrals)</span></p>
        {data.top_creators.length === 0 ? <p className="text-sm text-zinc-500">No creator data yet.</p> : (
          <ul className="space-y-2">
            {data.top_creators.map((t, i) => (
              <li key={t.user_id} data-testid={`top-creator-${i}`} className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-[#F5C462]/15 text-[#F5C462] font-bold text-xs flex items-center justify-center border border-[#F5C462]/30">{i+1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{t.name || `@${t.username}`} {t.is_verified && <ShieldCheck size={10} className="inline text-[#F5C462]"/>}</p>
                  <p className="text-[11px] text-zinc-400">@{t.username || "—"}</p>
                </div>
                <span className="text-sm text-white font-semibold">{(t.referrals_qualified || 0)} <span className="text-[11px] text-zinc-500 font-normal">referrals</span></span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-[10px] text-zinc-500">Generated at {new Date(data.generated_at).toLocaleString()}</p>
    </div>
  );
}

// ==================== BANNERS PANEL ====================
function BannersPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ title: "", image_url: "", link_url: "", is_published: false, sort_order: 0 });
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const { data } = await api.get("/admin/banners"); setRows(data.banners || []); } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); } };
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ title: "", image_url: "", link_url: "", is_published: false, sort_order: 0 }); setEditingId(null); };

  const save = async () => {
    if (!form.title.trim() || !form.image_url.trim()) return toast.error("Title & image URL required");
    setBusy(true);
    try {
      const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
      if (editingId) {
        await api.patch(`/admin/banners/${editingId}`, payload);
        toast.success("Banner updated");
      } else {
        await api.post("/admin/banners", payload);
        toast.success("Banner created");
      }
      resetForm();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const startEdit = (b) => { setEditingId(b.id); setForm({ title: b.title || "", image_url: b.image_url || "", link_url: b.link_url || "", is_published: !!b.is_published, sort_order: b.sort_order || 0 }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const togglePublish = async (b) => { await api.patch(`/admin/banners/${b.id}`, { title: b.title, image_url: b.image_url, link_url: b.link_url, is_published: !b.is_published, sort_order: b.sort_order || 0 }); toast.success(!b.is_published ? "Published" : "Unpublished"); load(); };
  const remove = async (b) => { if (!confirm(`Delete banner "${b.title}"?`)) return; await api.delete(`/admin/banners/${b.id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="banners-panel" className="space-y-4">
      <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="font-display font-semibold text-white flex items-center gap-2"><ImageIcon size={16} className="text-[#DC2626]"/> {editingId ? "Edit banner" : "New banner"}</p>
          {editingId && <button data-testid="banner-cancel-edit" onClick={resetForm} className="text-xs text-zinc-400 hover:text-white">Cancel edit</button>}
        </div>
        <Input data-testid="banner-title" placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        <Input data-testid="banner-image" placeholder="Image URL * (https://…)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        <Input data-testid="banner-link" placeholder="Link URL (optional)" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        <div className="grid grid-cols-2 gap-2 items-center">
          <div>
            <Label className="text-zinc-300 text-xs">Sort order</Label>
            <Input data-testid="banner-sort" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
          </div>
          <label data-testid="banner-publish-toggle" className="mt-4 h-10 flex items-center justify-between rounded-xl border border-zinc-800 bg-zinc-900/60 px-3 cursor-pointer">
            <span className="text-xs text-zinc-300">Published</span>
            <input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="accent-[#DC2626] w-4 h-4"/>
          </label>
        </div>
        {form.image_url && (
          <div className="rounded-xl overflow-hidden border border-zinc-800 max-h-48">
            <img src={form.image_url} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
          </div>
        )}
        <Button data-testid="banner-save" onClick={save} disabled={busy} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
          {busy ? <Loader2 className="animate-spin" size={14}/> : <><Save size={14} className="mr-1"/> {editingId ? "Update banner" : "Create banner"}</>}
        </Button>
      </div>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider text-zinc-400">All banners ({rows.length})</p>
        {rows.length === 0 ? <p className="text-sm text-zinc-500">No banners yet — create one above.</p> : rows.map(b => (
          <div key={b.id} data-testid={`banner-row-${b.id}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex items-center gap-3 flex-wrap">
            <div className="w-16 h-10 rounded-lg overflow-hidden bg-black border border-zinc-800 shrink-0">
              <img src={b.image_url} alt={b.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{b.title}</p>
              <p className="text-[11px] text-zinc-400 truncate">Order #{b.sort_order || 0}{b.link_url ? ` · ${b.link_url}` : ""}</p>
            </div>
            <span data-testid={`banner-status-${b.id}`} className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${b.is_published ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-800 text-zinc-400"}`}>{b.is_published ? "PUBLISHED" : "DRAFT"}</span>
            <div className="flex gap-1">
              <Button size="sm" data-testid={`banner-toggle-${b.id}`} onClick={() => togglePublish(b)} className={`h-8 rounded-full text-xs ${b.is_published ? "bg-amber-500/20 hover:bg-amber-500/30 text-amber-400" : "bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400"}`}>
                {b.is_published ? <><EyeOff size={12} className="mr-1"/>Unpublish</> : <><Send size={12} className="mr-1"/>Publish</>}
              </Button>
              <Button size="sm" data-testid={`banner-edit-${b.id}`} onClick={() => startEdit(b)} className="h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs"><Edit2 size={12} className="mr-1"/>Edit</Button>
              <Button size="sm" data-testid={`banner-delete-${b.id}`} onClick={() => remove(b)} className="h-8 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs"><Trash2 size={12} className="mr-1"/>Delete</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== CATEGORIES PANEL ====================
function CategoriesPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ key: "", label: "", is_disabled: false });
  const [editingKey, setEditingKey] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const { data } = await api.get("/admin/categories"); setRows(data.categories || []); } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); } };
  useEffect(() => { load(); }, []);

  // Seed from CATEGORIES constant if categories collection empty
  const seed = async () => {
    if (!confirm(`Seed ${CATEGORIES.length} default categories?`)) return;
    setBusy(true);
    try {
      for (const c of CATEGORIES) {
        await api.post("/admin/categories", { key: c.key, label: c.label, is_disabled: false });
      }
      toast.success(`Seeded ${CATEGORIES.length} categories`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed failed"); }
    finally { setBusy(false); }
  };

  const resetForm = () => { setForm({ key: "", label: "", is_disabled: false }); setEditingKey(null); };
  const save = async () => {
    if (!form.key.trim() || !form.label.trim()) return toast.error("Key & label required");
    if (!/^[a-z0-9_]+$/.test(form.key.trim())) return toast.error("Key: lowercase a-z, 0-9, _ only");
    setBusy(true);
    try {
      await api.post("/admin/categories", { key: form.key.trim(), label: form.label.trim(), is_disabled: form.is_disabled });
      toast.success(editingKey ? "Category updated" : "Category created");
      resetForm();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const toggle = async (key) => { const { data } = await api.patch(`/admin/categories/${key}/toggle`); toast.success(data.is_disabled ? "Disabled" : "Enabled"); load(); };
  const remove = async (key) => { if (!confirm(`Delete category "${key}"?`)) return; await api.delete(`/admin/categories/${key}`); toast.success("Deleted"); load(); };
  const startEdit = (c) => { setEditingKey(c.key); setForm({ key: c.key, label: c.label, is_disabled: !!c.is_disabled }); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div data-testid="categories-panel" className="space-y-4">
      <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="font-display font-semibold text-white flex items-center gap-2"><LayoutGrid size={16} className="text-[#DC2626]"/> {editingKey ? "Edit category" : "Add / update category"}</p>
          {rows.length === 0 && <Button size="sm" data-testid="cat-seed" onClick={seed} disabled={busy} className="h-8 rounded-full bg-[#F5C462] hover:bg-[#F5C462]/90 text-black text-xs font-semibold">{busy ? <Loader2 className="animate-spin" size={12}/> : <><Sparkles size={12} className="mr-1"/> Seed defaults</>}</Button>}
          {editingKey && <button data-testid="cat-cancel" onClick={resetForm} className="text-xs text-zinc-400 hover:text-white">Cancel</button>}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="cat-key" placeholder="Key (e.g. actor) *" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase() })} disabled={!!editingKey} className="bg-zinc-900 border-zinc-800"/>
          <Input data-testid="cat-label" placeholder="Label (e.g. Actor) *" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
          <input data-testid="cat-disabled" type="checkbox" checked={form.is_disabled} onChange={(e) => setForm({ ...form, is_disabled: e.target.checked })} className="accent-[#DC2626] w-4 h-4"/>
          Start disabled (hidden from signup / discovery)
        </label>
        <Button data-testid="cat-save" onClick={save} disabled={busy} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
          {busy ? <Loader2 className="animate-spin" size={14}/> : <><Plus size={14} className="mr-1"/> {editingKey ? "Update" : "Save"} category</>}
        </Button>
      </div>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider text-zinc-400">All categories ({rows.length})</p>
        {rows.length === 0 ? <p className="text-sm text-zinc-500">No categories in DB. Click "Seed defaults" above to populate the 25+ built-in list.</p> : rows.map(c => (
          <div key={c.key} data-testid={`cat-row-${c.key}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate text-white">{c.label}</p>
              <p className="text-[11px] text-zinc-400 truncate">key: <code className="text-zinc-300">{c.key}</code></p>
            </div>
            <span data-testid={`cat-status-${c.key}`} className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${c.is_disabled ? "bg-zinc-800 text-zinc-400" : "bg-emerald-500/20 text-emerald-400"}`}>{c.is_disabled ? "DISABLED" : "ACTIVE"}</span>
            <div className="flex gap-1">
              <Button size="sm" data-testid={`cat-toggle-${c.key}`} onClick={() => toggle(c.key)} className={`h-8 rounded-full text-xs ${c.is_disabled ? "bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400" : "bg-amber-500/20 hover:bg-amber-500/30 text-amber-400"}`}>
                {c.is_disabled ? <><Check size={12} className="mr-1"/>Enable</> : <><EyeOff size={12} className="mr-1"/>Disable</>}
              </Button>
              <Button size="sm" data-testid={`cat-edit-${c.key}`} onClick={() => startEdit(c)} className="h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs"><Edit2 size={12} className="mr-1"/>Edit</Button>
              <Button size="sm" data-testid={`cat-delete-${c.key}`} onClick={() => remove(c.key)} className="h-8 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs"><Trash2 size={12} className="mr-1"/>Delete</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== AI CONTENT HELPER ====================
function AiHelperPanel() {
  const [category, setCategory] = useState("actor");
  const [subCategory, setSubCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const generate = async () => {
    setBusy(true); setErr(null); setResult(null);
    try {
      const { data } = await api.post("/admin/ai/suggest-opportunity", { category, sub_category: subCategory.trim() || null });
      setResult(data.suggestion || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "AI generation failed");
    } finally { setBusy(false); }
  };

  const copyText = async () => {
    if (!result) return;
    const text = `${result.title}\n\n${result.description}\n\nDialogues:\n${(result.dialogues || []).map((d, i) => `${i+1}. ${d}`).join("\n")}`;
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); } catch { toast.error("Copy failed"); }
  };

  const publishAsOpportunity = async () => {
    if (!result?.title || !result?.description) return;
    setPublishing(true);
    try {
      const desc = `${result.description}\n\nAudition dialogues:\n${(result.dialogues || []).map((d, i) => `${i+1}. ${d}`).join("\n")}`;
      await api.post("/admin/opportunities", {
        title: result.title,
        category,
        sub_category: subCategory.trim() || null,
        description: desc,
        difficulty: "medium",
        duration: "",
        expiry: "",
        creator_pass_required: false,
        status: "draft",
        scheduled_at: "",
      });
      toast.success("Draft opportunity created — check Opportunities tab");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally { setPublishing(false); }
  };

  return (
    <div data-testid="ai-panel" className="space-y-4">
      <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Bot size={16} className="text-[#DC2626]"/>
          <p className="font-display font-semibold text-white">AI Content Helper</p>
          <span className="ml-auto text-[10px] rounded-full bg-[#F5C462]/15 text-[#F5C462] px-2 py-0.5 border border-[#F5C462]/30">Claude Sonnet</span>
        </div>
        <p className="text-xs text-zinc-400">Category select karein — AI generate karega title, description aur 3 Hinglish audition dialogues.</p>

        <div className="grid grid-cols-2 gap-2">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="ai-category" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Category"/></SelectTrigger>
            <SelectContent className="bg-[#180D0D] border-zinc-800 text-white max-h-64">
              {CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="ai-sub-category" placeholder="Sub-category (optional)" value={subCategory} onChange={(e) => setSubCategory(e.target.value)} className="bg-zinc-900 border-zinc-800"/>
        </div>

        <Button data-testid="ai-generate" onClick={generate} disabled={busy} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
          {busy ? <><Loader2 className="animate-spin mr-2" size={14}/> Generating…</> : <><Sparkles size={14} className="mr-1"/> Generate opportunity</>}
        </Button>
        {err && <p data-testid="ai-error" className="text-xs text-red-400">{err}</p>}
      </div>

      {result && (
        <div data-testid="ai-result" className="rounded-2xl border border-[#F5C462]/40 bg-gradient-to-br from-[#F5C462]/5 to-transparent p-4 space-y-3">
          <div className="flex items-center gap-2">
            <p className="text-xs uppercase tracking-wider text-[#F5C462]">AI suggestion</p>
            <div className="ml-auto flex gap-1">
              <Button size="sm" data-testid="ai-copy" onClick={copyText} className="h-7 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs"><Copy size={11} className="mr-1"/>Copy</Button>
              <Button size="sm" data-testid="ai-publish" onClick={publishAsOpportunity} disabled={publishing} className="h-7 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white text-xs font-semibold">
                {publishing ? <Loader2 className="animate-spin" size={11}/> : <><Send size={11} className="mr-1"/>Save as draft</>}
              </Button>
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Title</p>
            <p data-testid="ai-title" className="text-lg font-display font-semibold text-white mt-1">{result.title || "—"}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Description</p>
            <p data-testid="ai-description" className="text-sm text-zinc-200 mt-1 leading-relaxed whitespace-pre-line">{result.description || "—"}</p>
          </div>
          {(result.dialogues || []).length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Play size={10}/> Audition dialogues</p>
              <ol className="mt-2 space-y-2">
                {result.dialogues.map((d, i) => (
                  <li key={i} data-testid={`ai-dialogue-${i}`} className="rounded-xl border border-zinc-800 bg-black/40 p-3 text-sm text-zinc-100 leading-relaxed">
                    <span className="text-[#DC2626] font-bold mr-2">{i+1}.</span>{d}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TrainingAdsPanel() {
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    academy_name: "", category: "acting_schools", description: "",
    city: "", state: "", website_url: "", phone: "", whatsapp: "",
    status: "active",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  });
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);

  const load = async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : "";
      const { data } = await api.get(`/admin/training-ads${params}`);
      setRows(data.ads || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => {
    api.get("/training-ads/categories").then(r => setCategories(r.data.categories || []));
    load();
    // eslint-disable-next-line
  }, [statusFilter]);

  const resetForm = () => {
    setForm({
      academy_name: "", category: "acting_schools", description: "",
      city: "", state: "", website_url: "", phone: "", whatsapp: "",
      status: "active",
      start_date: new Date().toISOString().slice(0, 10),
      end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    });
    setLogoFile(null); setBannerFile(null); setEditingId(null);
  };

  const startEdit = (ad) => {
    setEditingId(ad.id);
    setForm({
      academy_name: ad.academy_name, category: ad.category, description: ad.description,
      city: ad.city, state: ad.state, website_url: ad.website_url,
      phone: ad.phone || "", whatsapp: ad.whatsapp || "", status: ad.status,
      start_date: ad.start_date, end_date: ad.end_date,
    });
    setLogoFile(null); setBannerFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.academy_name || !form.description || !form.city || !form.state || !form.website_url) {
      return toast.error("Academy, description, city, state and website are required");
    }
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/admin/training-ads/${editingId}`, form);
        toast.success("Advertisement updated");
      } else {
        if (!logoFile || !bannerFile) { setBusy(false); return toast.error("Logo & banner images are required"); }
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
        fd.append("logo", logoFile);
        fd.append("banner", bannerFile);
        await api.post("/admin/training-ads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Advertisement created");
      }
      resetForm();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const toggle = async (ad) => {
    try {
      const { data } = await api.post(`/admin/training-ads/${ad.id}/toggle`);
      toast.success(`Marked ${data.status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (ad) => {
    if (!confirm(`Delete advertisement for "${ad.academy_name}"?`)) return;
    try { await api.delete(`/admin/training-ads/${ad.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const catLabelOf = (k) => categories.find(c => c.key === k)?.label || k;

  return (
    <div data-testid="training-ads-panel" className="space-y-4">
      <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="font-display font-semibold text-white flex items-center gap-2"><GraduationCap size={16} className="text-[#F5C462]"/> {editingId ? "Edit training advertisement" : "New training advertisement"}</p>
          {editingId && <button data-testid="ta-cancel-edit" onClick={resetForm} className="text-xs text-zinc-400 hover:text-white">Cancel edit</button>}
        </div>

        <Input data-testid="ta-name" placeholder="Academy name *" value={form.academy_name} onChange={(e) => setForm({ ...form, academy_name: e.target.value })} className="bg-zinc-900 border-zinc-800"/>

        <div className="grid grid-cols-2 gap-2">
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="ta-category" className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Category"/></SelectTrigger>
            <SelectContent className="bg-[#180D0D] border-zinc-800 text-white max-h-64">
              {categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger data-testid="ta-status" className="bg-zinc-900 border-zinc-800"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-[#180D0D] border-zinc-800 text-white">
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Textarea data-testid="ta-desc" placeholder="Short description *" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800 min-h-[70px]" maxLength={400}/>

        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="ta-city" placeholder="City *" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
          <Input data-testid="ta-state" placeholder="State *" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        </div>

        <Input data-testid="ta-website" placeholder="Website URL * (https://…)" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} className="bg-zinc-900 border-zinc-800"/>

        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="ta-phone" placeholder="Phone (optional — enables Call button)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
          <Input data-testid="ta-whatsapp" placeholder="WhatsApp (optional — enables WA button)" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-zinc-300 text-xs">Start date</Label>
            <Input data-testid="ta-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
          </div>
          <div>
            <Label className="text-zinc-300 text-xs">End date</Label>
            <Input data-testid="ta-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
          </div>
        </div>

        {!editingId && (
          <div className="grid grid-cols-2 gap-2">
            <label className="rounded-xl border-2 border-dashed border-zinc-700 hover:border-[#F5C462]/50 h-20 flex flex-col items-center justify-center text-xs text-zinc-300 cursor-pointer transition-colors">
              <ImageIcon size={16} className={`mb-1 ${logoFile ? "text-emerald-400" : "text-zinc-500"}`}/>
              <span>{logoFile ? logoFile.name.slice(0, 22) : "Upload logo * (≤2MB)"}</span>
              <input data-testid="ta-logo" type="file" accept="image/*" className="hidden" onChange={(e) => setLogoFile(e.target.files?.[0] || null)}/>
            </label>
            <label className="rounded-xl border-2 border-dashed border-zinc-700 hover:border-[#F5C462]/50 h-20 flex flex-col items-center justify-center text-xs text-zinc-300 cursor-pointer transition-colors">
              <ImageIcon size={16} className={`mb-1 ${bannerFile ? "text-emerald-400" : "text-zinc-500"}`}/>
              <span>{bannerFile ? bannerFile.name.slice(0, 22) : "Upload banner * (≤5MB)"}</span>
              <input data-testid="ta-banner" type="file" accept="image/*" className="hidden" onChange={(e) => setBannerFile(e.target.files?.[0] || null)}/>
            </label>
          </div>
        )}
        {editingId && <p className="text-[11px] text-zinc-500">Logo & banner cannot be replaced in edit — delete + recreate to change images.</p>}

        <Button data-testid="ta-save" onClick={save} disabled={busy} className="w-full h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-white font-semibold">
          {busy ? <Loader2 className="animate-spin" size={14}/> : <><Save size={14} className="mr-1"/> {editingId ? "Update advertisement" : "Create advertisement"}</>}
        </Button>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs uppercase tracking-wider text-zinc-400">All advertisements ({rows.length})</p>
        <div className="flex flex-wrap gap-1">
          {[{k: "", label: "All"}, {k: "active", label: "Active"}, {k: "inactive", label: "Inactive"}].map(s => (
            <button key={s.k || "all"} data-testid={`ta-filter-${s.k || "all"}`} onClick={() => setStatusFilter(s.k)} className={`h-7 px-2.5 rounded-full text-[11px] font-medium border ${statusFilter === s.k ? "border-[#F5C462] bg-[#F5C462]/15 text-[#F5C462]" : "border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:border-[#F5C462]/40"}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {rows.length === 0 ? <p className="text-sm text-zinc-500">No advertisements yet.</p> : rows.map(ad => (
          <div key={ad.id} data-testid={`ta-row-${ad.id}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex items-center gap-3 flex-wrap">
            {ad.logo_path && (
              <img src={`${BACKEND_ADMIN}/api/training-ads/media/${ad.logo_path}`} alt="" className="w-10 h-10 rounded-lg object-cover border border-zinc-800 shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate text-white">{ad.academy_name}</p>
              <p className="text-[11px] text-zinc-400 truncate">{catLabelOf(ad.category)} · {ad.city}, {ad.state} · {ad.start_date} → {ad.end_date}</p>
              <a href={ad.website_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#F5C462] hover:underline truncate inline-flex items-center gap-1"><ExternalLink size={10}/> {ad.website_url}</a>
            </div>
            <span data-testid={`ta-status-${ad.id}`} className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${ad.status === "active" ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-800 text-zinc-400"}`}>{ad.status.toUpperCase()}</span>
            <div className="flex gap-1">
              <Button size="sm" data-testid={`ta-toggle-${ad.id}`} onClick={() => toggle(ad)} className={`h-8 rounded-full text-xs ${ad.status === "active" ? "bg-amber-500/20 hover:bg-amber-500/30 text-amber-400" : "bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400"}`}>
                {ad.status === "active" ? <><EyeOff size={12} className="mr-1"/>Deactivate</> : <><Check size={12} className="mr-1"/>Activate</>}
              </Button>
              <Button size="sm" data-testid={`ta-edit-${ad.id}`} onClick={() => startEdit(ad)} className="h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs"><Edit2 size={12} className="mr-1"/>Edit</Button>
              <Button size="sm" data-testid={`ta-delete-${ad.id}`} onClick={() => remove(ad)} className="h-8 rounded-full bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs"><Trash2 size={12} className="mr-1"/>Delete</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const BACKEND_ADMIN = process.env.REACT_APP_BACKEND_URL;

// ==================== SPONSORED ADS PANEL ====================
function SponsoredAdsPanel() {
  const [meta, setMeta] = useState({ categories: [], placements: [] });
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    business_name: "", category: "acting_schools", description: "",
    website_url: "", whatsapp: "", phone: "",
    placement: "home", priority: 0, status: "active",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  });
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/sponsored-ads");
    setRows(data.ads || []);
  };
  useEffect(() => { api.get("/sponsored-ads/meta").then(r => setMeta(r.data)).catch(() => {}); load(); }, []);

  const resetForm = () => {
    setForm({ business_name: "", category: meta.categories[0]?.key || "acting_schools", description: "", website_url: "", whatsapp: "", phone: "", placement: "home", priority: 0, status: "active", start_date: new Date().toISOString().slice(0, 10), end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10) });
    setLogoFile(null); setBannerFile(null); setEditingId(null);
  };

  const startEdit = (ad) => {
    setEditingId(ad.id);
    setForm({ business_name: ad.business_name, category: ad.category, description: ad.description, website_url: ad.website_url, whatsapp: ad.whatsapp || "", phone: ad.phone || "", placement: ad.placement, priority: ad.priority || 0, status: ad.status, start_date: ad.start_date, end_date: ad.end_date });
    setLogoFile(null); setBannerFile(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.business_name || !form.description || !form.website_url) return toast.error("Name, description, website required");
    setBusy(true);
    try {
      if (editingId) {
        await api.patch(`/admin/sponsored-ads/${editingId}`, { ...form, priority: Number(form.priority) });
        toast.success("Sponsored ad updated");
      } else {
        if (!logoFile || !bannerFile) { setBusy(false); return toast.error("Logo & banner required"); }
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
        fd.append("logo", logoFile); fd.append("banner", bannerFile);
        await api.post("/admin/sponsored-ads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Sponsored ad created");
      }
      resetForm(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const toggle = async (ad) => { await api.post(`/admin/sponsored-ads/${ad.id}/toggle`); toast.success("Toggled"); load(); };
  const remove = async (ad) => { if (!confirm(`Delete "${ad.business_name}"?`)) return; await api.delete(`/admin/sponsored-ads/${ad.id}`); toast.success("Deleted"); load(); };

  const catLabelOf = (k) => meta.categories.find(c => c.key === k)?.label || k;

  return (
    <div data-testid="sponsored-ads-panel" className="space-y-4">
      <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4 space-y-3">
        <p className="font-display font-semibold text-white flex items-center gap-2"><Megaphone size={16} className="text-[#F5C462]"/> {editingId ? "Edit sponsored ad" : "New sponsored ad"}</p>
        <Input data-testid="sa-name" placeholder="Business name *" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        <div className="grid grid-cols-2 gap-2">
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="sa-category" className="bg-zinc-900 border-zinc-800"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-[#180D0D] border-zinc-800 text-white max-h-64">
              {meta.categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={form.placement} onValueChange={(v) => setForm({ ...form, placement: v })}>
            <SelectTrigger data-testid="sa-placement" className="bg-zinc-900 border-zinc-800"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-[#180D0D] border-zinc-800 text-white">
              {meta.placements.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Textarea data-testid="sa-desc" placeholder="Short description *" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800" maxLength={400}/>
        <Input data-testid="sa-website" placeholder="Website URL *" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="sa-phone" placeholder="Phone (Call btn)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
          <Input data-testid="sa-whatsapp" placeholder="WhatsApp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="text-zinc-300 text-xs">Priority</Label><Input data-testid="sa-priority" type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="bg-zinc-900 border-zinc-800"/></div>
          <div><Label className="text-zinc-300 text-xs">Start</Label><Input data-testid="sa-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-zinc-900 border-zinc-800"/></div>
          <div><Label className="text-zinc-300 text-xs">End</Label><Input data-testid="sa-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-zinc-900 border-zinc-800"/></div>
        </div>
        {!editingId && (
          <div className="grid grid-cols-2 gap-2">
            <label className="rounded-xl border-2 border-dashed border-zinc-700 h-16 flex items-center justify-center text-xs text-zinc-300 cursor-pointer"><span>{logoFile ? logoFile.name.slice(0, 20) : "Logo * (≤2MB)"}</span><input data-testid="sa-logo" type="file" accept="image/*" className="hidden" onChange={(e) => setLogoFile(e.target.files?.[0])}/></label>
            <label className="rounded-xl border-2 border-dashed border-zinc-700 h-16 flex items-center justify-center text-xs text-zinc-300 cursor-pointer"><span>{bannerFile ? bannerFile.name.slice(0, 20) : "Banner * (≤5MB)"}</span><input data-testid="sa-banner" type="file" accept="image/*" className="hidden" onChange={(e) => setBannerFile(e.target.files?.[0])}/></label>
          </div>
        )}
        <Button data-testid="sa-save" onClick={save} disabled={busy} className="w-full h-11 rounded-full bg-[#DC2626] text-white font-semibold">{busy ? <Loader2 className="animate-spin" size={14}/> : (editingId ? "Update" : "Create")}</Button>
        {editingId && <button data-testid="sa-cancel" onClick={resetForm} className="w-full text-xs text-zinc-400 hover:text-white">Cancel edit</button>}
      </div>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider text-zinc-400">All sponsored ads ({rows.length})</p>
        {rows.map(ad => (
          <div key={ad.id} data-testid={`sa-row-${ad.id}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex items-center gap-3 flex-wrap">
            {ad.logo_path && <img src={`${BACKEND_ADMIN}/api/sponsored-ads/media/${ad.logo_path}`} alt="" className="w-10 h-10 rounded-lg object-cover border border-zinc-800 shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }}/>}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate text-white">{ad.business_name} <span className="text-[10px] text-[#F5C462] ml-1">[{ad.placement}]</span></p>
              <p className="text-[11px] text-zinc-400 truncate">{catLabelOf(ad.category)} · P{ad.priority || 0} · Imp {ad.impressions || 0} / Clk {ad.clicks || 0} · CTR {ad.impressions ? ((100 * (ad.clicks || 0) / ad.impressions).toFixed(1)) : "0.0"}%</p>
            </div>
            <span data-testid={`sa-status-${ad.id}`} className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${ad.status === "active" ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-800 text-zinc-400"}`}>{ad.status.toUpperCase()}</span>
            <div className="flex gap-1">
              <Button size="sm" data-testid={`sa-toggle-${ad.id}`} onClick={() => toggle(ad)} className="h-8 rounded-full bg-amber-500/20 text-amber-400 text-xs">Toggle</Button>
              <Button size="sm" data-testid={`sa-edit-${ad.id}`} onClick={() => startEdit(ad)} className="h-8 rounded-full bg-zinc-800 text-zinc-300 text-xs"><Edit2 size={12}/></Button>
              <Button size="sm" data-testid={`sa-delete-${ad.id}`} onClick={() => remove(ad)} className="h-8 rounded-full bg-red-500/20 text-red-400 text-xs"><Trash2 size={12}/></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== FEATURED LISTINGS PANEL ====================
function FeaturedListingsPanel() {
  const [meta, setMeta] = useState({ categories: [] });
  const [rows, setRows] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    business_name: "", category: "acting_schools", description: "", website_url: "",
    city: "", state: "", priority: 0, status: "active",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  });
  const [logoFile, setLogoFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);

  const load = async () => { const { data } = await api.get("/admin/featured-listings"); setRows(data.listings || []); };
  useEffect(() => { api.get("/sponsored-ads/meta").then(r => setMeta(r.data)).catch(() => {}); load(); }, []);

  const resetForm = () => { setForm({ business_name: "", category: meta.categories[0]?.key || "acting_schools", description: "", website_url: "", city: "", state: "", priority: 0, status: "active", start_date: new Date().toISOString().slice(0, 10), end_date: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10) }); setLogoFile(null); setCoverFile(null); setEditingId(null); };

  const startEdit = (f) => { setEditingId(f.id); setForm({ business_name: f.business_name, category: f.category, description: f.description, website_url: f.website_url, city: f.city, state: f.state, priority: f.priority || 0, status: f.status, start_date: f.start_date, end_date: f.end_date }); setLogoFile(null); setCoverFile(null); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const save = async () => {
    if (!form.business_name || !form.description || !form.website_url || !form.city || !form.state) return toast.error("All required fields must be filled");
    setBusy(true);
    try {
      if (editingId) { await api.patch(`/admin/featured-listings/${editingId}`, { ...form, priority: Number(form.priority) }); toast.success("Updated"); }
      else {
        if (!logoFile || !coverFile) { setBusy(false); return toast.error("Logo & cover required"); }
        const fd = new FormData(); Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? "")); fd.append("logo", logoFile); fd.append("cover", coverFile);
        await api.post("/admin/featured-listings", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Featured listing created");
      }
      resetForm(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const toggle = async (f) => { await api.post(`/admin/featured-listings/${f.id}/toggle`); toast.success("Toggled"); load(); };
  const remove = async (f) => { if (!confirm(`Delete "${f.business_name}"?`)) return; await api.delete(`/admin/featured-listings/${f.id}`); toast.success("Deleted"); load(); };
  const bump = async (f, delta) => { await api.patch(`/admin/featured-listings/${f.id}`, { priority: (f.priority || 0) + delta }); load(); };

  const catLabelOf = (k) => meta.categories.find(c => c.key === k)?.label || k;

  return (
    <div data-testid="featured-panel" className="space-y-4">
      <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4 space-y-3">
        <p className="font-display font-semibold text-white flex items-center gap-2"><Sparkles size={16} className="text-[#F5C462]"/> {editingId ? "Edit featured listing" : "New featured listing"}</p>
        <Input data-testid="fl-name" placeholder="Business name *" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
          <SelectTrigger data-testid="fl-category" className="bg-zinc-900 border-zinc-800"><SelectValue/></SelectTrigger>
          <SelectContent className="bg-[#180D0D] border-zinc-800 text-white max-h-64">
            {meta.categories.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Textarea data-testid="fl-desc" placeholder="Short description *" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-900 border-zinc-800" maxLength={400}/>
        <Input data-testid="fl-website" placeholder="Website URL *" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        <div className="grid grid-cols-2 gap-2">
          <Input data-testid="fl-city" placeholder="City *" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
          <Input data-testid="fl-state" placeholder="State *" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="bg-zinc-900 border-zinc-800"/>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="text-zinc-300 text-xs">Priority</Label><Input data-testid="fl-priority" type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="bg-zinc-900 border-zinc-800"/></div>
          <div><Label className="text-zinc-300 text-xs">Start</Label><Input data-testid="fl-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-zinc-900 border-zinc-800"/></div>
          <div><Label className="text-zinc-300 text-xs">End</Label><Input data-testid="fl-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-zinc-900 border-zinc-800"/></div>
        </div>
        {!editingId && (
          <div className="grid grid-cols-2 gap-2">
            <label className="rounded-xl border-2 border-dashed border-zinc-700 h-16 flex items-center justify-center text-xs text-zinc-300 cursor-pointer"><span>{logoFile ? logoFile.name.slice(0, 20) : "Logo * (≤2MB)"}</span><input data-testid="fl-logo" type="file" accept="image/*" className="hidden" onChange={(e) => setLogoFile(e.target.files?.[0])}/></label>
            <label className="rounded-xl border-2 border-dashed border-zinc-700 h-16 flex items-center justify-center text-xs text-zinc-300 cursor-pointer"><span>{coverFile ? coverFile.name.slice(0, 20) : "Cover * (≤5MB)"}</span><input data-testid="fl-cover" type="file" accept="image/*" className="hidden" onChange={(e) => setCoverFile(e.target.files?.[0])}/></label>
          </div>
        )}
        <Button data-testid="fl-save" onClick={save} disabled={busy} className="w-full h-11 rounded-full bg-[#DC2626] text-white font-semibold">{busy ? <Loader2 className="animate-spin" size={14}/> : (editingId ? "Update" : "Create")}</Button>
        {editingId && <button data-testid="fl-cancel" onClick={resetForm} className="w-full text-xs text-zinc-400 hover:text-white">Cancel edit</button>}
      </div>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider text-zinc-400">All featured listings ({rows.length}) · sorted by priority ↓</p>
        {rows.map(f => (
          <div key={f.id} data-testid={`fl-row-${f.id}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex items-center gap-3 flex-wrap">
            {f.logo_path && <img src={`${BACKEND_ADMIN}/api/featured-listings/media/${f.logo_path}`} alt="" className="w-10 h-10 rounded-lg object-cover border border-zinc-800 shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }}/>}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate text-white">{f.business_name}</p>
              <p className="text-[11px] text-zinc-400 truncate">{catLabelOf(f.category)} · {f.city}, {f.state} · priority <b className="text-[#F5C462]">{f.priority || 0}</b></p>
            </div>
            <span data-testid={`fl-status-${f.id}`} className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${f.status === "active" ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-800 text-zinc-400"}`}>{f.status.toUpperCase()}</span>
            <div className="flex gap-1">
              <Button size="sm" data-testid={`fl-up-${f.id}`} onClick={() => bump(f, +1)} className="h-8 w-8 rounded-full bg-[#F5C462]/15 text-[#F5C462] text-xs p-0">↑</Button>
              <Button size="sm" data-testid={`fl-down-${f.id}`} onClick={() => bump(f, -1)} className="h-8 w-8 rounded-full bg-zinc-800 text-zinc-300 text-xs p-0">↓</Button>
              <Button size="sm" data-testid={`fl-toggle-${f.id}`} onClick={() => toggle(f)} className="h-8 rounded-full bg-amber-500/20 text-amber-400 text-xs">Toggle</Button>
              <Button size="sm" data-testid={`fl-edit-${f.id}`} onClick={() => startEdit(f)} className="h-8 rounded-full bg-zinc-800 text-zinc-300 text-xs"><Edit2 size={12}/></Button>
              <Button size="sm" data-testid={`fl-delete-${f.id}`} onClick={() => remove(f)} className="h-8 rounded-full bg-red-500/20 text-red-400 text-xs"><Trash2 size={12}/></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== TRAINING AD ENQUIRIES PANEL ====================
function EnquiriesPanel() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [analytics, setAnalytics] = useState(null);

  const load = async () => {
    const params = filter ? `?status=${filter}` : "";
    const { data } = await api.get(`/admin/training-ad-enquiries${params}`);
    setRows(data.enquiries || []);
  };
  useEffect(() => { load(); api.get("/admin/training-ads/analytics").then(r => setAnalytics(r.data)); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, s) => { await api.post(`/admin/training-ad-enquiries/${id}/status?status=${s}`); toast.success(`Marked ${s}`); load(); };
  const exportCsv = () => { window.open(`${BACKEND_ADMIN}/api/admin/training-ad-enquiries.csv?_=${Date.now()}`, "_blank"); };

  return (
    <div data-testid="enquiries-panel" className="space-y-4">
      {analytics && (
        <div data-testid="training-analytics" className="rounded-2xl border border-[#F5C462]/30 bg-gradient-to-r from-[#F5C462]/8 to-transparent p-4">
          <p className="text-xs uppercase tracking-wider text-[#F5C462] mb-2 font-semibold">Training ads · overall</p>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><p className="text-zinc-400 text-[11px]">Impressions</p><p className="text-white font-semibold text-lg">{analytics.totals.impressions.toLocaleString("en-IN")}</p></div>
            <div><p className="text-zinc-400 text-[11px]">Clicks</p><p className="text-emerald-400 font-semibold text-lg">{analytics.totals.clicks.toLocaleString("en-IN")}</p></div>
            <div><p className="text-zinc-400 text-[11px]">CTR</p><p className="text-[#F5C462] font-semibold text-lg">{analytics.totals.ctr}%</p></div>
          </div>
          {analytics.ads.length > 0 && (
            <div className="mt-3 space-y-1">
              {analytics.ads.slice(0, 5).map(a => (
                <div key={a.id} className="flex items-center justify-between text-[11px] text-zinc-300">
                  <span className="truncate flex-1">{a.academy_name}</span>
                  <span className="text-zinc-500">{a.impressions} imp · {a.clicks} clk · <span className="text-[#F5C462]">{a.ctr}%</span></span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {[{k: "", label: "All"}, {k: "new", label: "New"}, {k: "contacted", label: "Contacted"}, {k: "closed", label: "Closed"}].map(s => (
            <button key={s.k || "all"} data-testid={`enq-filter-${s.k || "all"}`} onClick={() => setFilter(s.k)} className={`h-8 px-3 rounded-full text-xs font-medium border ${filter === s.k ? "border-[#DC2626] bg-[#DC2626]/15 text-[#DC2626]" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`}>{s.label}</button>
          ))}
        </div>
        <Button data-testid="enq-export" onClick={exportCsv} className="h-8 rounded-full bg-[#F5C462] hover:bg-[#F5C462]/90 text-black font-semibold text-xs"><FileClock size={12} className="mr-1"/>Export CSV</Button>
      </div>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider text-zinc-400">Enquiries ({rows.length})</p>
        {rows.length === 0 ? <p className="text-sm text-zinc-500">No enquiries yet.</p> : rows.map(e => (
          <div key={e.id} data-testid={`enq-row-${e.id}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{e.name} <span className="text-zinc-500 text-[11px]">· {e.phone}</span></p>
              <p className="text-[11px] text-zinc-400 truncate">For <span className="text-[#F5C462]">{e.academy_name}</span> · {new Date(e.created_at).toLocaleString()}</p>
              {e.message && <p className="text-[11px] text-zinc-300 mt-1 line-clamp-2">{e.message}</p>}
            </div>
            <span data-testid={`enq-status-${e.id}`} className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${e.status === "new" ? "bg-[#DC2626]/20 text-[#DC2626]" : e.status === "contacted" ? "bg-amber-500/20 text-amber-400" : "bg-zinc-700/40 text-zinc-400"}`}>{e.status.toUpperCase()}</span>
            <div className="flex gap-1">
              {e.status === "new" && <Button size="sm" data-testid={`enq-contacted-${e.id}`} onClick={() => setStatus(e.id, "contacted")} className="h-8 rounded-full bg-amber-500/20 text-amber-400 text-xs">Contacted</Button>}
              {e.status !== "closed" && <Button size="sm" data-testid={`enq-close-${e.id}`} onClick={() => setStatus(e.id, "closed")} className="h-8 rounded-full bg-zinc-700/40 text-zinc-300 text-xs">Close</Button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ==================== AD DASHBOARD PANEL ====================
function AdDashboardPanel() {
  const [data, setData] = useState(null);
  const [renewBusy, setRenewBusy] = useState(null);
  useEffect(() => { api.get("/admin/ads/dashboard").then(r => setData(r.data)); }, []);
  const renew = async (id) => {
    setRenewBusy(id);
    try { const { data: r } = await api.post(`/admin/sponsored-ads/${id}/renew?days=30`); toast.success(`Renewed until ${r.end_date.slice(0,10)}`); const { data: d } = await api.get("/admin/ads/dashboard"); setData(d); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setRenewBusy(null); }
  };
  if (!data) return <p className="text-sm text-zinc-400">Loading dashboard…</p>;
  return (
    <div data-testid="ad-dashboard-panel" className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric testid="ad-active"    label="Active ads"       value={data.total_active}    icon={Megaphone}     accent="emerald"/>
        <Metric testid="ad-scheduled" label="Scheduled"        value={data.scheduled}       icon={CalendarClock} accent="amber"/>
        <Metric testid="ad-expiring"  label="Expiring in 7d"   value={data.expiring_soon}   icon={AlertTriangle} accent="red"/>
        <Metric testid="ad-expired"   label="Expired"          value={data.expired}         icon={Archive}       accent="zinc"/>
        <Metric testid="ad-impressions" label="Impressions"    value={data.total_impressions.toLocaleString("en-IN")} icon={Eye}      accent="zinc"/>
        <Metric testid="ad-clicks"    label="Clicks"           value={data.total_clicks.toLocaleString("en-IN")}      icon={TrendingUp} accent="emerald"/>
        <Metric testid="ad-ctr"       label="Overall CTR"      value={`${data.overall_ctr}%`} icon={Sparkles} accent="gold"/>
      </div>
      <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4">
        <p className="text-xs uppercase tracking-wider text-[#F5C462] font-semibold mb-3 flex items-center gap-1"><TrendingUp size={12}/> Top-performing sponsored ads</p>
        {data.top_performing.length === 0 ? <p className="text-sm text-zinc-500">No ads yet.</p> : (
          <ul className="space-y-2">
            {data.top_performing.map((a, i) => (
              <li key={a.id} data-testid={`top-ad-${i}`} className="flex items-center gap-3 rounded-xl border border-zinc-800 bg-black/30 p-3">
                <span className="w-6 h-6 rounded-full bg-[#F5C462]/15 text-[#F5C462] font-bold text-xs flex items-center justify-center border border-[#F5C462]/30">{i+1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{a.business_name}</p>
                  <p className="text-[11px] text-zinc-400 truncate">{a.placement} · {a.category} · P{a.priority || 0} · <span className="text-emerald-400">{a.impressions || 0} imp</span> · <span className="text-[#DC2626]">{a.clicks || 0} clk</span> · <span className="text-[#F5C462]">{a.ctr}%</span></p>
                </div>
                <span className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${a.status === "active" ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-800 text-zinc-400"}`}>{a.status.toUpperCase()}</span>
                <Button size="sm" data-testid={`renew-${a.id}`} onClick={() => renew(a.id)} disabled={renewBusy === a.id} className="h-8 rounded-full bg-[#F5C462]/15 hover:bg-[#F5C462]/25 text-[#F5C462] text-xs">
                  {renewBusy === a.id ? <Loader2 className="animate-spin" size={12}/> : <><RotateCcw size={12} className="mr-1"/>Renew 30d</>}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-[10px] text-zinc-500">Generated {new Date(data.generated_at).toLocaleString()}</p>
    </div>
  );
}

// ==================== OPPORTUNITY ANALYTICS PANEL ====================
function OppAnalyticsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/opportunities/analytics").then(r => setData(r.data)); }, []);
  const exportCsv = () => window.open(`${BACKEND_ADMIN}/api/admin/opportunities/analytics.csv?_=${Date.now()}`, "_blank");
  if (!data) return <p className="text-sm text-zinc-400">Loading…</p>;
  const maxApplies = Math.max(1, ...data.opportunities.map(o => o.applies));
  return (
    <div data-testid="opp-analytics-panel" className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Metric testid="oa-views"   label="Total views"   value={data.totals.views.toLocaleString("en-IN")}   icon={Eye}         accent="zinc"/>
        <Metric testid="oa-saves"   label="Total saves"   value={data.totals.saves.toLocaleString("en-IN")}   icon={Archive}     accent="red"/>
        <Metric testid="oa-applies" label="Total applies" value={data.totals.applies.toLocaleString("en-IN")} icon={TrendingUp}  accent="emerald"/>
      </div>
      <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-wider text-[#F5C462] font-semibold">Per-opportunity funnel</p>
          <Button data-testid="oa-export" onClick={exportCsv} className="h-8 rounded-full bg-[#F5C462] text-black font-semibold text-xs"><FileClock size={12} className="mr-1"/>Export CSV</Button>
        </div>
        {data.opportunities.length === 0 ? <p className="text-sm text-zinc-500">No opportunities yet.</p> : (
          <ul className="space-y-3">
            {data.opportunities.map(o => (
              <li key={o.id} data-testid={`oa-row-${o.id}`} className="rounded-xl border border-zinc-800 bg-black/30 p-3">
                <div className="flex items-baseline justify-between gap-2 mb-1.5">
                  <p className="text-sm font-semibold text-white truncate">{o.title}</p>
                  <span className="text-[10px] text-zinc-500">{o.category} · {o.status}</span>
                </div>
                <div className="h-2 rounded-full bg-zinc-900 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-[#DC2626]" style={{ width: `${Math.min(100, (o.applies / maxApplies) * 100)}%` }}/>
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[11px]">
                  <span className="text-zinc-400">{o.views} views · {o.saves} saves · <span className="text-emerald-400">{o.applies} applies</span></span>
                  <span className="text-[#F5C462]">save {o.save_rate}% · apply {o.apply_rate}%</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ==================== ADVERTISE LEADS PANEL ====================
const ADV_PLACEMENTS = [
  { k: "home", label: "Home" },
  { k: "top_banner", label: "Top banner" },
  { k: "mid_feed", label: "Mid-feed" },
  { k: "bottom_banner", label: "Bottom banner" },
  { k: "between_categories", label: "Between categories" },
  { k: "between_search", label: "Between search" },
  { k: "search", label: "Search" },
  { k: "profile", label: "Profile" },
  { k: "performance_detail", label: "Performance detail" },
  { k: "full_width", label: "Full-width" },
  { k: "category", label: "Category" },
  { k: "between_opportunities", label: "Between opportunities" },
];

function ActivateAdDialog({ req, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 86400e3).toISOString().slice(0, 10);
  const [placement, setPlacement] = useState(req.preferred_placement && ADV_PLACEMENTS.find(p => p.k === req.preferred_placement) ? req.preferred_placement : "home");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(in30);
  const [priority, setPriority] = useState(100);
  const [topInfo, setTopInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  // Fetch current top-priority tier for the picked placement whenever it changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/sponsored-ads/top-priority?placement=${placement}`);
        if (!cancelled) {
          setTopInfo(data);
          // auto-bump priority so activated ad wins (unless admin manually overrode below top)
          setPriority(p => (p <= (data?.top_priority || 0) ? (data.top_priority + 10) : p));
        }
      } catch { if (!cancelled) setTopInfo(null); }
    })();
    return () => { cancelled = true; };
  }, [placement]);
  const submit = async () => {
    if (!req.banner_path) return toast.error("This request has no banner — ask advertiser to resend with banner image");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("placement", placement); fd.append("start_date", startDate); fd.append("end_date", endDate); fd.append("priority", String(priority));
      await api.post(`/admin/advertise-requests/${req.id}/activate`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Advertisement activated — will auto-appear on start date and auto-expire on end date");
      onDone?.(); onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Activation failed"); }
    finally { setBusy(false); }
  };
  const willBeInvisible = topInfo && priority < (topInfo.top_priority || 0);
  return (
    <div data-testid="activate-ad-dialog" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-md rounded-2xl border border-zinc-800 bg-[#0A0605] p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-sm font-semibold">Activate advertisement</h3>
          <button data-testid="activate-close" onClick={onClose} className="text-zinc-400 hover:text-white"><X size={16}/></button>
        </div>
        <p className="text-[11px] text-zinc-400">Advertiser: <span className="text-zinc-200 font-medium">{req.business_name}</span> ({req.contact_person}) · Requested placement: {req.preferred_placement || "—"} · Duration: {req.duration || "—"}</p>
        {!req.banner_path && <p className="text-[11px] text-amber-400">⚠️ No banner uploaded — activation will fail. Contact advertiser first.</p>}
        <div>
          <Label className="text-zinc-300 text-xs">Placement</Label>
          <Select value={placement} onValueChange={setPlacement}>
            <SelectTrigger data-testid="activate-placement" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-[#180D0D] border-zinc-800 text-white max-h-64">
              {ADV_PLACEMENTS.map(p => <SelectItem key={p.k} value={p.k}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {topInfo && (
          <div data-testid="activate-priority-preview" className="rounded-xl border border-zinc-800 bg-[#180D0D] px-3 py-2 text-[11px]">
            <p className="text-zinc-300">
              <span className="text-[#F5C462] font-semibold">Live tier at {placement}:</span> priority <span className="font-semibold text-white">{topInfo.top_priority || 0}</span>
              {topInfo.tier_size ? <span className="text-zinc-400"> · {topInfo.tier_size} ad{topInfo.tier_size !== 1 ? "s" : ""} sharing this tier</span> : <span className="text-zinc-400"> · empty</span>}
              {topInfo.top_business && <span className="text-zinc-500"> · top: {topInfo.top_business}</span>}
            </p>
            <p className="text-zinc-400 mt-1">Recommend priority ≥ <span className="text-emerald-400 font-semibold">{(topInfo.top_priority || 0) + 1}</span> so this ad wins impressions on day one.</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-zinc-300 text-xs">Start date</Label><Input data-testid="activate-start" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-1 bg-zinc-900 border-zinc-800"/></div>
          <div><Label className="text-zinc-300 text-xs">End date</Label><Input data-testid="activate-end" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="mt-1 bg-zinc-900 border-zinc-800"/></div>
        </div>
        <div>
          <Label className="text-zinc-300 text-xs">Priority (higher = shown first — seeded demo ads use priority=70)</Label>
          <Input data-testid="activate-priority" type="number" value={priority} onChange={e => setPriority(parseInt(e.target.value) || 0)} className="mt-1 bg-zinc-900 border-zinc-800"/>
          {willBeInvisible && <p data-testid="activate-invisible-warning" className="text-[11px] text-amber-400 mt-1">⚠️ Priority {priority} is below the current top tier ({topInfo.top_priority}) — this ad will not be shown until higher-priority ads expire.</p>}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onClose} className="rounded-full bg-zinc-800 hover:bg-zinc-700 text-white text-xs h-8">Cancel</Button>
          <Button data-testid="activate-submit" onClick={submit} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8">
            {busy ? <Loader2 className="animate-spin mr-1" size={12}/> : <Play size={12} className="mr-1"/>} Activate
          </Button>
        </div>
      </div>
    </div>
  );
}

function AdvertiseLeadsPanel() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [activateReq, setActivateReq] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const load = async () => { const params = filter ? `?status=${filter}` : ""; const { data } = await api.get(`/admin/advertise-requests${params}`); setRows(data.requests || []); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  const setStatus = async (id, s) => { await api.post(`/admin/advertise-requests/${id}/status?status=${s}`); toast.success(`Marked ${s}`); load(); };
  const exportCsv = () => window.open(`${BACKEND_ADMIN}/api/admin/advertise-requests.csv?_=${Date.now()}`, "_blank");
  return (
    <div data-testid="advertise-leads-panel" className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {[{k: "", label: "All"}, {k: "new", label: "New"}, {k: "contacted", label: "Contacted"}, {k: "activated", label: "Activated"}, {k: "closed", label: "Closed"}].map(s => (
            <button key={s.k || "all"} data-testid={`adv-filter-${s.k || "all"}`} onClick={() => setFilter(s.k)} className={`h-8 px-3 rounded-full text-xs font-medium border ${filter === s.k ? "border-[#F5C462] bg-[#F5C462]/15 text-[#F5C462]" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`}>{s.label}</button>
          ))}
        </div>
        <Button data-testid="adv-export" onClick={exportCsv} className="h-8 rounded-full bg-[#F5C462] text-black font-semibold text-xs"><FileClock size={12} className="mr-1"/>Export CSV</Button>
      </div>
      {rows.length === 0 ? <p className="text-sm text-zinc-500">No advertise requests yet.</p> : rows.map(r => (
        <div key={r.id} data-testid={`adv-row-${r.id}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{r.business_name || r.contact_person || "—"} <span className="text-zinc-500 text-[11px]">· {r.contact_person}</span></p>
            <p className="text-[11px] text-zinc-400 truncate">{r.category || "—"} · {r.city || "—"} · {r.phone || r.email || "—"} · {new Date(r.created_at).toLocaleString()}</p>
            <p className="text-[11px] text-zinc-400 truncate">Placement: {r.preferred_placement || "—"} · Duration: {r.duration || "—"} {r.website && <>· <a href={r.website} target="_blank" rel="noopener noreferrer" className="text-[#F5C462] underline">Website</a></>}</p>
            {r.goal && <p className="text-[11px] text-zinc-300 mt-1 line-clamp-2">Goal: {r.goal}</p>}
            {r.message && <p className="text-[11px] text-zinc-300 mt-1 line-clamp-2">Notes: {r.message}</p>}
            {r.banner_path && <button data-testid={`adv-banner-${r.id}`} onClick={(e) => { e.stopPropagation(); setBannerPreview(`${BACKEND_ADMIN}/api/advertise-requests/media/${r.banner_path}`); }} className="text-[11px] text-[#F5C462] underline inline-flex items-center gap-1 mt-1 bg-transparent border-0 cursor-pointer p-0 hover:text-white transition-colors"><ImageIcon size={10}/>View banner</button>}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <span data-testid={`adv-status-${r.id}`} className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${r.status === "new" ? "bg-[#DC2626]/20 text-[#DC2626]" : r.status === "contacted" ? "bg-amber-500/20 text-amber-400" : r.status === "activated" ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-700/40 text-zinc-400"}`}>{(r.status || "new").toUpperCase()}</span>
            <div className="flex gap-1 flex-wrap justify-end">
              {r.status === "new" && <Button size="sm" data-testid={`adv-contacted-${r.id}`} onClick={() => setStatus(r.id, "contacted")} className="h-7 rounded-full bg-amber-500/20 text-amber-400 text-[11px] px-2">Contacted</Button>}
              {r.status !== "activated" && r.status !== "closed" && <Button size="sm" data-testid={`adv-activate-${r.id}`} onClick={() => setActivateReq(r)} className="h-7 rounded-full bg-emerald-600 text-white text-[11px] px-2"><Play size={10} className="mr-1"/>Activate</Button>}
              {r.status !== "closed" && <Button size="sm" data-testid={`adv-close-${r.id}`} onClick={() => setStatus(r.id, "closed")} className="h-7 rounded-full bg-zinc-700/40 text-zinc-300 text-[11px] px-2">Close</Button>}
            </div>
          </div>
        </div>
      ))}
      {activateReq && <ActivateAdDialog req={activateReq} onClose={() => setActivateReq(null)} onDone={load}/>}
      {bannerPreview && (
        <div data-testid="banner-preview-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setBannerPreview(null)}>
          <div onClick={(e) => e.stopPropagation()} className="relative max-w-2xl w-full">
            <button data-testid="banner-preview-close" onClick={() => setBannerPreview(null)} className="absolute -top-10 right-0 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white"><X size={16}/></button>
            <img src={bannerPreview} alt="banner" className="w-full h-auto rounded-2xl border border-zinc-800 bg-black" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
            <a href={bannerPreview} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-[11px] text-[#F5C462] hover:text-white underline">Open in new tab</a>
          </div>
        </div>
      )}
    </div>
  );
}

function SupportTicketsPanel() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : "";
      const { data } = await api.get(`/admin/support-tickets${params}`);
      setRows(data.tickets || []);
    } catch { toast.error("Failed to load tickets"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  const setStatus = async (id, status) => {
    try { await api.post(`/admin/support-tickets/${id}/status?status=${status}`); toast.success(`Marked ${status}`); load(); }
    catch { toast.error("Failed"); }
  };
  const kindEmoji = { bug: "🐞", help: "💬", feedback: "✨" };
  return (
    <div data-testid="support-tickets-panel" className="space-y-3">
      <div className="flex gap-2 items-center">
        <HelpCircle size={14} className="text-[#F5C462]"/>
        <p className="text-sm font-semibold text-white">Support Tickets</p>
        <div className="flex gap-1 ml-auto">
          {[{k: "", label: "All"}, {k: "new", label: "New"}, {k: "in_progress", label: "In Progress"}, {k: "resolved", label: "Resolved"}, {k: "closed", label: "Closed"}].map(s => (
            <button key={s.k || "all"} data-testid={`ticket-filter-${s.k || "all"}`} onClick={() => setFilter(s.k)} className={`h-8 px-3 rounded-full text-[11px] font-medium border ${filter === s.k ? "border-[#F5C462] bg-[#F5C462]/15 text-[#F5C462]" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`}>{s.label}</button>
          ))}
        </div>
      </div>
      {loading ? <p className="text-sm text-zinc-500 py-6 text-center">Loading...</p> :
       rows.length === 0 ? <p className="text-sm text-zinc-500 py-6 text-center">No tickets in this window.</p> : (
        <div className="space-y-2">
          {rows.map(t => (
            <div key={t.id} data-testid={`ticket-${t.id}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3">
              <div className="flex items-start gap-3 flex-wrap">
                <span className="text-lg">{kindEmoji[t.kind] || "📩"}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{t.subject}</p>
                  <p className="text-[11px] text-zinc-500">@{t.username || "unknown"} · {t.email || "no email"} · {new Date(t.created_at).toLocaleString()}</p>
                  <p className="text-[11px] text-zinc-300 mt-1.5 line-clamp-3 whitespace-pre-wrap">{t.message}</p>
                </div>
                <span className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${t.status === "new" ? "bg-[#DC2626]/20 text-[#DC2626]" : t.status === "in_progress" ? "bg-amber-500/20 text-amber-400" : t.status === "resolved" ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-700/40 text-zinc-300"}`}>{(t.status || "new").toUpperCase()}</span>
              </div>
              <div className="mt-2 flex gap-1 flex-wrap">
                {t.status === "new" && <Button size="sm" data-testid={`ticket-progress-${t.id}`} onClick={() => setStatus(t.id, "in_progress")} className="h-7 rounded-full bg-amber-500/20 text-amber-400 text-[11px] px-2">Start</Button>}
                {t.status !== "resolved" && <Button size="sm" data-testid={`ticket-resolve-${t.id}`} onClick={() => setStatus(t.id, "resolved")} className="h-7 rounded-full bg-emerald-600 text-white text-[11px] px-2">Resolve</Button>}
                {t.status !== "closed" && <Button size="sm" data-testid={`ticket-close-${t.id}`} onClick={() => setStatus(t.id, "closed")} className="h-7 rounded-full bg-zinc-700/40 text-zinc-300 text-[11px] px-2">Close</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AppealsPanel() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : "";
      const { data } = await api.get(`/admin/appeals${params}`);
      setRows(data.appeals || []);
    } catch { toast.error("Failed to load appeals"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  const setStatus = async (id, status) => {
    try { await api.post(`/admin/appeals/${id}/status?status=${status}`); toast.success(`Marked ${status}`); load(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div data-testid="appeals-panel" className="space-y-3">
      <div className="flex gap-2 items-center">
        <ShieldCheck size={14} className="text-[#F5C462]"/>
        <p className="text-sm font-semibold text-white">Legal & Appeals</p>
        <div className="flex gap-1 ml-auto">
          {[{k: "", label: "All"}, {k: "new", label: "New"}, {k: "in_review", label: "In Review"}, {k: "resolved", label: "Resolved"}, {k: "rejected", label: "Rejected"}].map(s => (
            <button key={s.k || "all"} data-testid={`appeal-filter-${s.k || "all"}`} onClick={() => setFilter(s.k)} className={`h-8 px-3 rounded-full text-[11px] font-medium border ${filter === s.k ? "border-[#F5C462] bg-[#F5C462]/15 text-[#F5C462]" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`}>{s.label}</button>
          ))}
        </div>
      </div>
      {loading ? <p className="text-sm text-zinc-500 py-6 text-center">Loading...</p> :
       rows.length === 0 ? <p className="text-sm text-zinc-500 py-6 text-center">No appeals in this window.</p> : (
        <div className="space-y-2">
          {rows.map(a => (
            <div key={a.id} data-testid={`appeal-${a.id}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">Appeal: {a.reason}</p>
                  <p className="text-[11px] text-zinc-500">@{a.username || "guest"} · {a.contact_email || "no email"} · {new Date(a.created_at).toLocaleString()}</p>
                  <p className="text-[11px] text-zinc-300 mt-1.5 line-clamp-4 whitespace-pre-wrap">{a.message}</p>
                  {a.admin_notes && <p className="text-[11px] text-amber-300 mt-1">Notes: {a.admin_notes}</p>}
                </div>
                <span className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${a.status === "new" ? "bg-[#DC2626]/20 text-[#DC2626]" : a.status === "in_review" ? "bg-amber-500/20 text-amber-400" : a.status === "resolved" ? "bg-emerald-500/20 text-emerald-400" : "bg-zinc-700/40 text-zinc-300"}`}>{(a.status || "new").toUpperCase()}</span>
              </div>
              <div className="mt-2 flex gap-1 flex-wrap">
                {a.status === "new" && <Button size="sm" data-testid={`appeal-review-${a.id}`} onClick={() => setStatus(a.id, "in_review")} className="h-7 rounded-full bg-amber-500/20 text-amber-400 text-[11px] px-2">Review</Button>}
                {a.status !== "resolved" && <Button size="sm" data-testid={`appeal-resolve-${a.id}`} onClick={() => setStatus(a.id, "resolved")} className="h-7 rounded-full bg-emerald-600 text-white text-[11px] px-2">Resolve</Button>}
                {a.status !== "rejected" && <Button size="sm" data-testid={`appeal-reject-${a.id}`} onClick={() => setStatus(a.id, "rejected")} className="h-7 rounded-full bg-red-500/20 text-red-400 text-[11px] px-2">Reject</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function SearchAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const load = async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/search-analytics?days=${d}`);
      setData(data);
    } catch { toast.error("Failed to load search analytics"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);
  const catLbl = (k) => ({actor: "Actor", singer: "Singer", dancer: "Dancer", model: "Model", "voice-artist": "Voice Artist", comedian: "Comedian", poet: "Poet"}[k] || k);
  return (
    <div data-testid="search-analytics-panel" className="space-y-4">
      <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Search size={14} className="text-[#F5C462]"/>
          <p className="text-sm font-semibold text-white">Search Analytics</p>
        </div>
        <div className="flex gap-1 ml-auto">
          {[7, 30, 90].map(d => (
            <button key={d} data-testid={`sa-window-${d}`} onClick={() => setDays(d)} className={`h-8 px-3 rounded-full text-[11px] font-medium border ${days === d ? "border-[#F5C462] bg-[#F5C462]/15 text-[#F5C462]" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`}>{d}d</button>
          ))}
        </div>
      </div>
      {loading || !data ? <p className="text-sm text-zinc-500 py-6 text-center">Loading...</p> : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard label="Total searches" value={data.total_searches} accent="#F5C462"/>
            <StatCard label="Top keywords" value={data.top_keywords.length} accent="#DC2626"/>
            <StatCard label="Top categories" value={data.top_categories.length} accent="#10B981"/>
            <StatCard label="Zero-hit queries" value={data.zero_hit_queries.length} accent="#F59E0B"/>
          </div>

          <RankList
            testid="top-keywords"
            title="Top 10 keywords"
            icon={Search}
            rows={data.top_keywords}
            renderRow={(r, i) => (
              <div className="flex items-center gap-3 py-2">
                <span className="text-[#F5C462] font-bold text-sm w-6">{i + 1}.</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium truncate">{r.q}</p>
                  <p className="text-[11px] text-zinc-500">avg {r.avg_results} results · last {new Date(r.last).toLocaleDateString()}</p>
                </div>
                <span className="text-xs rounded-full bg-[#DC2626]/20 text-[#DC2626] px-2 py-0.5 font-semibold">{r.count}</span>
              </div>
            )}
          />

          <RankList
            testid="top-categories"
            title="Top 10 categories"
            icon={Folder}
            rows={data.top_categories}
            renderRow={(r, i) => (
              <div className="flex items-center gap-3 py-2">
                <span className="text-[#F5C462] font-bold text-sm w-6">{i + 1}.</span>
                <p className="flex-1 text-sm text-white font-medium truncate">{catLbl(r.category)}</p>
                <span className="text-xs rounded-full bg-[#DC2626]/20 text-[#DC2626] px-2 py-0.5 font-semibold">{r.count}</span>
              </div>
            )}
          />

          {data.top_cities.length > 0 && (
            <RankList
              testid="top-cities"
              title="Top cities"
              icon={MapPin}
              rows={data.top_cities}
              renderRow={(r, i) => (
                <div className="flex items-center gap-3 py-2">
                  <span className="text-[#F5C462] font-bold text-sm w-6">{i + 1}.</span>
                  <p className="flex-1 text-sm text-white font-medium truncate">{r.city}</p>
                  <span className="text-xs rounded-full bg-[#DC2626]/20 text-[#DC2626] px-2 py-0.5 font-semibold">{r.count}</span>
                </div>
              )}
            />
          )}

          {data.zero_hit_queries.length > 0 && (
            <RankList
              testid="zero-hit-queries"
              title="Zero-hit queries (opportunities to grow content)"
              icon={AlertTriangle}
              rows={data.zero_hit_queries}
              accent="#F59E0B"
              renderRow={(r, i) => (
                <div className="flex items-center gap-3 py-2">
                  <span className="text-amber-400 font-bold text-sm w-6">{i + 1}.</span>
                  <p className="flex-1 text-sm text-white font-medium truncate">{r.q}</p>
                  <span className="text-xs rounded-full bg-amber-500/20 text-amber-400 px-2 py-0.5 font-semibold">{r.count}</span>
                </div>
              )}
            />
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3">
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className="text-2xl font-bold mt-1" style={{color: accent}}>{value}</p>
    </div>
  );
}

function RankList({ title, icon: Icon, rows, renderRow, testid, accent = "#F5C462" }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} style={{color: accent}}/>
        <p className="text-sm font-semibold text-white">{title}</p>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-zinc-500 py-3">No data in this window yet.</p>
      ) : (
        <div className="divide-y divide-zinc-800/60">
          {rows.map((r, i) => <div key={i}>{renderRow(r, i)}</div>)}
        </div>
      )}
    </div>
  );
}


function NotificationsLogPanel() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ whatsapp_configured: false, email_configured: false, high_priority_events: [] });
  const [channel, setChannel] = useState("");
  const [eventFilter, setEventFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const params = channel ? `?channel=${channel}&limit=200` : "?limit=200";
      const { data } = await api.get(`/admin/notifications-log${params}`);
      setRows(data.logs || []);
      setMeta({
        whatsapp_configured: data.whatsapp_configured,
        email_configured: data.email_configured,
        high_priority_events: data.high_priority_events || [],
        event_channel_map: data.event_channel_map || {},
      });
    } catch (e) { toast.error("Failed to load notification log"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [channel]);
  const filtered = rows.filter(r => (!eventFilter || r.event === eventFilter) && (!statusFilter || r.status === statusFilter));
  const uniqueEvents = Array.from(new Set(rows.map(r => r.event).filter(Boolean))).sort();
  const uniqueStatuses = Array.from(new Set(rows.map(r => r.status).filter(Boolean))).sort();
  const statusCls = (s) => ({
    sent: "bg-emerald-500/20 text-emerald-400",
    mock: "bg-zinc-700/40 text-zinc-300",
    MOCKED: "bg-zinc-700/40 text-zinc-300",
    failed: "bg-red-500/20 text-red-400",
    skipped_no_phone: "bg-amber-500/20 text-amber-400",
    skipped_no_email: "bg-amber-500/20 text-amber-400",
  }[s] || "bg-zinc-700/40 text-zinc-300");
  return (
    <div data-testid="notif-log-panel" className="space-y-3">
      <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Bell size={14} className="text-[#F5C462]"/>
          <p className="text-sm font-semibold text-white">Notification History</p>
        </div>
        <span data-testid="notif-log-wa-config" className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${meta.whatsapp_configured ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}`}>WhatsApp: {meta.whatsapp_configured ? "LIVE" : "MOCK"}</span>
        <span data-testid="notif-log-em-config" className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${meta.email_configured ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}`}>Email: {meta.email_configured ? "LIVE" : "MOCK"}</span>
        <Button data-testid="notif-log-refresh" onClick={load} className="ml-auto h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white text-xs"><RotateCcw size={12} className="mr-1"/>Refresh</Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[11px] text-zinc-500 uppercase tracking-wider">Channel:</span>
        {[{k: "", label: "All"}, {k: "whatsapp", label: "WhatsApp"}, {k: "email", label: "Email"}].map(c => (
          <button key={c.k || "all"} data-testid={`notif-log-ch-${c.k || "all"}`} onClick={() => setChannel(c.k)} className={`h-7 px-3 rounded-full text-[11px] font-medium border ${channel === c.k ? "border-[#F5C462] bg-[#F5C462]/15 text-[#F5C462]" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`}>{c.label}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[11px] text-zinc-500 uppercase tracking-wider">Event:</span>
        <button data-testid="notif-log-ev-all" onClick={() => setEventFilter("")} className={`h-7 px-3 rounded-full text-[11px] font-medium border ${!eventFilter ? "border-[#F5C462] bg-[#F5C462]/15 text-[#F5C462]" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`}>All</button>
        {uniqueEvents.map(ev => (
          <button key={ev} data-testid={`notif-log-ev-${ev}`} onClick={() => setEventFilter(ev)} className={`h-7 px-3 rounded-full text-[11px] font-medium border ${eventFilter === ev ? "border-[#F5C462] bg-[#F5C462]/15 text-[#F5C462]" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`} title={(meta.event_channel_map?.[ev] || []).join(", ")}>{ev}</button>
        ))}
        {uniqueStatuses.length > 0 && (
          <>
            <span className="text-[11px] text-zinc-500 uppercase tracking-wider ml-2">Status:</span>
            <button data-testid="notif-log-st-all" onClick={() => setStatusFilter("")} className={`h-7 px-3 rounded-full text-[11px] font-medium border ${!statusFilter ? "border-[#F5C462] bg-[#F5C462]/15 text-[#F5C462]" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`}>All</button>
            {uniqueStatuses.map(s => (
              <button key={s} data-testid={`notif-log-st-${s}`} onClick={() => setStatusFilter(s)} className={`h-7 px-3 rounded-full text-[11px] font-medium border ${statusFilter === s ? "border-[#F5C462] bg-[#F5C462]/15 text-[#F5C462]" : "border-zinc-800 bg-zinc-900 text-zinc-300"}`}>{s}</button>
            ))}
          </>
        )}
      </div>

      {loading ? <p className="text-sm text-zinc-500 py-6 text-center">Loading...</p> :
       filtered.length === 0 ? <p className="text-sm text-zinc-500 py-6 text-center">No entries match these filters.</p> : (
        <div className="space-y-2">
          {filtered.map(r => (
            <div key={r.id || `${r.created_at}-${r.user_id}`} data-testid={`notif-log-row-${r.id || ""}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-3 flex flex-wrap items-start gap-3">
              <span className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${r._channel === "whatsapp" ? "bg-[#25D366]/20 text-[#25D366]" : "bg-[#3B82F6]/20 text-[#60A5FA]"}`}>{(r._channel || "?").toUpperCase()}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">
                  <span className="font-semibold">{r.event || "-"}</span>
                  <span className="text-zinc-500 text-[11px]"> - {r.to || r.phone || "no recipient"}</span>
                </p>
                <p className="text-[11px] text-zinc-500">{new Date(r.created_at).toLocaleString()}{r.subject && <> - {r.subject}</>}{r.sid && <> - sid: {r.sid}</>}{r.email_id && <> - id: {r.email_id}</>}</p>
                {r.error && <p className="text-[11px] text-red-400 mt-1 line-clamp-2">Error: {r.error}</p>}
              </div>
              <span data-testid={`notif-log-status-${r.id || ""}`} className={`text-[10px] rounded-full px-2 py-0.5 font-semibold ${statusCls(r.status)}`}>{(r.status || "-").toUpperCase()}</span>
            </div>
          ))}
          <p className="text-[11px] text-zinc-600 text-center pt-2">Showing {filtered.length} of {rows.length} logs</p>
        </div>
      )}
    </div>
  );
}


function AuditPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/audit-log").then(r => setRows(r.data.entries || [])); }, []);
  return <div data-testid="audit-panel" className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-4">
    <p className="text-xs uppercase tracking-wider text-[#DC2626] flex items-center gap-1 mb-3"><FileClock size={12}/> Admin audit log</p>
    {rows.length === 0 ? <p className="text-sm text-zinc-400">No moderation actions logged yet.</p> : (
      <ul className="space-y-2">
        {rows.map(r => (
          <li key={r.id} data-testid={`audit-${r.id}`} className="rounded-xl border border-zinc-800 bg-black/40 p-3 text-xs">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="font-semibold text-white">{r.action}</span>
              <span className="text-[10px] rounded-full bg-zinc-800 text-zinc-400 px-2 py-0.5">{r.target_type}:{r.target_id?.slice(0,10)}</span>
              <span className="text-zinc-500 ml-auto">{new Date(r.created_at).toLocaleString()}</span>
            </div>
            <p className="text-zinc-400 mt-1">by <span className="text-zinc-200">{r.actor_email || r.actor_name || r.actor_id}</span>{r.reason && ` — ${r.reason}`}</p>
          </li>
        ))}
      </ul>
    )}
  </div>;
}

